<script setup>
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  onMounted,
  onBeforeUnmount,
  computed,
  ref,
  shallowRef,
  toRefs,
  nextTick,
} from 'vue';
import { useMessageSelection } from 'dashboard/composables/useMessageSelection.js';
import { useTimeoutFn } from '@vueuse/core';
import { provideMessageContext } from './provider.js';
import {
  aiReplyDisplayName,
  conversationAttendantIdentity,
  isUnattributedWameMessage,
} from './messageAuthor.js';
import { isGifMessage } from 'dashboard/helper/gifMediaHelper';
import { isAudioAttachment } from 'dashboard/helper/audioAttachmentHelper';
import { isLottieStickerAttachment } from 'dashboard/helper/lottieStickerPlayer';
import {
  isGroupConversation,
  stripGroupSenderPrefix,
} from './groupParticipant.js';
import { useTrack } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { emitter } from 'shared/helpers/mitt';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { LocalStorage } from 'shared/helpers/localStorage';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  MESSAGE_TYPES,
  ATTACHMENT_TYPES,
  MESSAGE_VARIANTS,
  SENDER_TYPES,
  ORIENTATION,
  MESSAGE_STATUS,
  CONTENT_TYPES,
} from './constants';

import Avatar from 'next/avatar/Avatar.vue';

import TextBubble from './bubbles/Text/Index.vue';
import ActivityBubble from './bubbles/Activity.vue';
import ImageBubble from './bubbles/Image.vue';
import LottieStickerBubble from './bubbles/LottieSticker.vue';
import ImageAlbumBubble from './bubbles/ImageAlbum.vue';
import StickerAlbumBubble from './bubbles/StickerAlbum.vue';
import FileBubble from './bubbles/File.vue';
import AudioBubble from './bubbles/Audio.vue';
import VideoBubble from './bubbles/Video.vue';
import EmbedBubble from './bubbles/Embed.vue';
import InstagramStoryBubble from './bubbles/InstagramStory.vue';
import EmailBubble from './bubbles/Email/Index.vue';
import UnsupportedBubble from './bubbles/Unsupported.vue';
import ContactBubble from './bubbles/Contact.vue';
import DyteBubble from './bubbles/Dyte.vue';
import LocationBubble from './bubbles/Location.vue';
import CSATBubble from './bubbles/CSAT.vue';
import FormBubble from './bubbles/Form.vue';
import VoiceCallBubble from './bubbles/VoiceCall.vue';
import WameMediaProgress from './bubbles/WameMediaProgress.vue';
// Aceleres: bolha de possivel falta pausada temporariamente.
// import AceleresPossibleAbsenceAlert from './bubbles/AceleresPossibleAbsenceAlert.vue';

import MessageError from './MessageError.vue';
import ContextMenu from 'dashboard/modules/conversations/components/MessageContextMenu.vue';
import {
  findContextMenuTrigger,
  getDashboardZoom,
  fixedPosition,
  computeMenuAnchor,
  getMobileFocusedMessageCenter,
  getMobileFocusedMessageHorizontalOffset,
  isMobileMessageMenu,
} from './contextMenuAnchor.js';
import {
  layoutToVisual,
  getVisualViewport,
} from 'dashboard/helper/dashboardZoom';
import MessageReactions from './MessageReactions.vue';
import MessageReactionsModal from './MessageReactionsModal.vue';
import MessageReactionBar from 'dashboard/components/widgets/conversation/MessageReactionBar.vue';
import { useBranding } from 'shared/composables/useBranding';
import { isMessagePinned } from 'dashboard/helper/pinnedMessages';

/**
 * @typedef {Object} Attachment
 * @property {number} id - Unique identifier for the attachment
 * @property {number} messageId - ID of the associated message
 * @property {'image'|'audio'|'video'|'file'|'location'|'fallback'|'share'|'story_mention'|'contact'|'ig_reel'} fileType - Type of the attachment (file or image)
 * @property {number} accountId - ID of the associated account
 * @property {string|null} extension - File extension
 * @property {string} dataUrl - URL to access the full attachment data
 * @property {string} thumbUrl - URL to access the thumbnail version
 * @property {number} fileSize - Size of the file in bytes
 * @property {number|null} width - Width of the image if applicable
 * @property {number|null} height - Height of the image if applicable
 */

/**
 * @typedef {Object} Sender
 * @property {Object} additional_attributes - Additional attributes of the sender
 * @property {Object} custom_attributes - Custom attributes of the sender
 * @property {string} email - Email of the sender
 * @property {number} id - ID of the sender
 * @property {string|null} identifier - Identifier of the sender
 * @property {string} name - Name of the sender
 * @property {string|null} phone_number - Phone number of the sender
 * @property {string} thumbnail - Thumbnail URL of the sender
 * @property {string} type - Type of sender
 */

/**
 * @typedef {Object} ContentAttributes
 * @property {string} externalError - an error message to be shown if the message failed to send
 */

/**
 * @typedef {Object} Props
 * @property {('sent'|'delivered'|'read'|'failed'|'progress')} status - The delivery status of the message
 * @property {ContentAttributes} [contentAttributes={}] - Additional attributes of the message content
 * @property {Attachment[]} [attachments=[]] - The attachments associated with the message
 * @property {Sender|null} [sender=null] - The sender information
 * @property {boolean} [private=false] - Whether the message is private
 * @property {number|null} [senderId=null] - The ID of the sender
 * @property {number} createdAt - Timestamp when the message was created
 * @property {number} currentUserId - The ID of the current user
 * @property {number} id - The unique identifier for the message
 * @property {number} messageType - The type of message (must be one of MESSAGE_TYPES)
 * @property {string|null} [error=null] - Error message if the message failed to send
 * @property {string|null} [senderType=null] - The type of the sender
 * @property {string} content - The message content
 * @property {boolean} [groupWithNext=false] - Whether the message should be grouped with the next message
 * @property {Object|null} [inReplyTo=null] - The message to which this message is a reply
 * @property {boolean} [isEmailInbox=false] - Whether the message is from an email inbox
 * @property {number} conversationId - The ID of the conversation to which the message belongs
 * @property {number} inboxId - The ID of the inbox to which the message belongs
 */

// eslint-disable-next-line vue/define-macros-order
const props = defineProps({
  id: { type: Number, required: true },
  messageType: {
    type: Number,
    required: true,
    validator: value => Object.values(MESSAGE_TYPES).includes(value),
  },
  status: {
    type: String,
    required: true,
    validator: value => Object.values(MESSAGE_STATUS).includes(value),
  },
  attachments: { type: Array, default: () => [] },
  content: { type: String, default: null },
  contentAttributes: { type: Object, default: () => ({}) },
  contentType: {
    type: String,
    default: 'text',
    validator: value => Object.values(CONTENT_TYPES).includes(value),
  },
  conversationId: { type: Number, required: true },
  createdAt: { type: Number, required: true }, // eslint-disable-line vue/no-unused-properties
  currentUserId: { type: Number, required: true }, // eslint-disable-line vue/no-unused-properties
  groupWithNext: { type: Boolean, default: false },
  inboxId: { type: Number, default: null }, // eslint-disable-line vue/no-unused-properties
  inboxSupportsReplyTo: { type: Object, default: () => ({}) },
  inReplyTo: { type: Object, default: null },
  isEmailInbox: { type: Boolean, default: false },
  private: { type: Boolean, default: false },
  additionalAttributes: { type: Object, default: () => ({}) }, // eslint-disable-line vue/no-unused-properties
  sender: { type: Object, default: null },
  senderId: { type: Number, default: null },
  senderType: { type: String, default: null },
  sourceId: { type: String, default: '' }, // eslint-disable-line vue/no-unused-properties
  historyMetadata: { type: Object, default: () => ({}) }, // eslint-disable-line vue/no-unused-properties
  // Aceleres: quando essa mensagem eh o "wrapper" de um album (de imagens/
  // videos OU de stickers) agrupado em MessageList.vue, esse array traz
  // TODAS as mensagens do run (inclusive esta). ImageAlbumBubble e
  // StickerAlbumBubble consomem via useMessageContext().
  albumMessages: { type: Array, default: null }, // eslint-disable-line vue/no-unused-properties
});

const emit = defineEmits(['retry']);

const {
  isSelectionMode,
  isMessageSelected,
  toggleMessageSelection,
  exitSelectionMode,
} = useMessageSelection();
const isSelected = computed(() => isMessageSelected(props.id));
let suppressNextClick = false;
let suppressNextClickTimer = null;
let suppressNextClickHandler = null;

function clearSuppressedClick() {
  suppressNextClick = false;
  if (suppressNextClickTimer) clearTimeout(suppressNextClickTimer);
  suppressNextClickTimer = null;
  if (suppressNextClickHandler) {
    document.removeEventListener('click', suppressNextClickHandler, true);
    suppressNextClickHandler = null;
  }
}

function armSuppressedClick() {
  clearSuppressedClick();
  suppressNextClick = true;
  // O menu vive em um Teleport no body. Em alguns celulares, o clique
  // sintetizado ao soltar a pressao longa cai no botao que apareceu sob o
  // dedo, fora da bolha. A captura no documento consome somente esse clique.
  suppressNextClickHandler = event => {
    if (!suppressNextClick) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    clearSuppressedClick();
  };
  document.addEventListener('click', suppressNextClickHandler, true);
}

function messageSelectionPayloadFor(message) {
  const source = message || {};

  return {
    id: source.id,
    content: source.content,
    attachments: source.attachments || [],
    messageType: source.messageType ?? source.message_type,
    status: source.status,
    contentAttributes:
      source.contentAttributes ?? source.content_attributes ?? {},
    conversationId: source.conversationId ?? source.conversation_id,
    createdAt: source.createdAt ?? source.created_at,
    sourceId: source.sourceId ?? source.source_id ?? '',
    private: source.private ?? false,
  };
}

function messageSelectionPayload() {
  return messageSelectionPayloadFor(props);
}

function handleMessageClick(e) {
  if (suppressNextClick) {
    e.preventDefault();
    e.stopPropagation();
    clearSuppressedClick();
    return;
  }
  if (e.target.closest('.aceleres-bubble-smile, .aceleres-bubble-chevron'))
    return;
  if (!isSelectionMode.value || props.messageType === MESSAGE_TYPES.ACTIVITY)
    return;
  e.preventDefault();
  e.stopPropagation();
  toggleMessageSelection(messageSelectionPayload());
}

// Aceleres 25/08/2026: o foco visual e exclusivo do mobile. A mensagem real
// continua na lista; uma copia inerte flutua sobre o fundo desfocado, sem
// remontar o componente nem provocar salto de scroll.
const isMobileViewport = ref(false);
let mobileViewportQuery = null;
const syncMobileViewport = event => {
  isMobileViewport.value = event.matches;
};

onMounted(() => {
  mobileViewportQuery = window.matchMedia(
    '(max-width: 767px) and (pointer: coarse)'
  );
  isMobileViewport.value = mobileViewportQuery.matches;
  mobileViewportQuery.addEventListener('change', syncMobileViewport);
});

onBeforeUnmount(() => {
  mobileViewportQuery?.removeEventListener('change', syncMobileViewport);
});

const contextMenuPosition = ref({});
// Aceleres 10/09/2026 — SO MOBILE: o par de figurinhas (StickerAlbum.vue) e
// UMA bolha, mas cada figurinha e uma mensagem propria. No desktop cada tile
// tem a sua flechinha e abre o menu do proprio sticker; no mobile so existe o
// toque longo, que cai neste componente — entao ele focava as DUAS figurinhas
// e agia sempre na ultima do par. Aqui guardamos QUAL tile foi tocado (o
// `data-message-id` que StickerAlbum.vue poe em cada tile) e o resto do fluxo
// — copia focada, menu, reacao e resposta — passa a apontar so para ela.
// Fora do mobile isto fica sempre `null` e nada muda.
const focusedStickerTileId = ref(null);
const showBackgroundHighlight = ref(false);
const showContextMenu = ref(false);

function resolveStickerTileId(e) {
  if (!isMobileViewport.value) return null;
  const tile =
    e?.target?.closest?.('.acl-sticker-album__tile') ||
    e?.currentTarget?.closest?.('.acl-sticker-album__tile');
  return tile?.dataset?.messageId || null;
}

const focusedStickerMessage = computed(() => {
  if (!focusedStickerTileId.value || !Array.isArray(props.albumMessages))
    return null;
  return (
    props.albumMessages.find(
      message => String(message.id) === String(focusedStickerTileId.value)
    ) || null
  );
});

let focusedMessageClone = null;
const focusedMessageClasses = [
  '!fixed',
  '!z-[2147482650]',
  '!m-0',
  '!bg-transparent',
  '!p-0',
  'pointer-events-none',
];

function removeFocusedMessageClone() {
  focusedMessageClone?.remove();
  focusedMessageClone = null;
}

function showFocusedMessageClone(e) {
  removeFocusedMessageClone();
  if (!isMobileViewport.value) return;

  const source =
    e?.currentTarget?.closest?.('.message-bubble-container') ||
    e?.target?.closest?.('.message-bubble-container');
  const focusedCenter = Number(contextMenuPosition.value.focusedCenter);
  if (!source || !Number.isFinite(focusedCenter)) return;

  const sourceRect = source.getBoundingClientRect();
  const sourceBubble = source.querySelector('.right-bubble, .left-bubble');
  const sourceBubbleRect = sourceBubble?.getBoundingClientRect();
  const zoom = getDashboardZoom();
  const viewport = getVisualViewport();
  const clone = source.cloneNode(true);
  clone.removeAttribute('id');
  clone
    .querySelectorAll('[id]')
    .forEach(element => element.removeAttribute('id'));
  clone.classList.remove('aceleres-context-open');
  clone.classList.add('aceleres-context-clone', ...focusedMessageClasses);
  clone.setAttribute('aria-hidden', 'true');
  clone.setAttribute('inert', '');
  // A copia mantem a grade inteira do par para nao mudar a geometria ja
  // calculada (`focusedCenter` vem da bolha toda); as figurinhas nao tocadas
  // apenas ficam invisiveis, de modo que so a escolhida flutue sobre o fundo.
  if (focusedStickerTileId.value) {
    clone.querySelectorAll('.acl-sticker-album__tile').forEach(tile => {
      if (tile.dataset?.messageId !== focusedStickerTileId.value) {
        tile.style.visibility = 'hidden';
      }
    });
  }
  clone.style.top = '0px';
  clone.style.left = fixedPosition(sourceRect.left, zoom);
  clone.style.width = fixedPosition(sourceRect.width, zoom);
  // A cópia inteira diminui em telas baixas. Assim a mídia, a legenda e o
  // horário mantêm a mesma geometria, com espaço para reações e ações.
  const availableHeight = Math.max(
    layoutToVisual(120, zoom),
    viewport.height - layoutToVisual(62 + 12 + 144 + 22, zoom)
  );
  const scale = Math.min(
    1,
    availableHeight / (sourceBubbleRect?.height || sourceRect.height)
  );
  if (scale < 1) {
    clone.style.transformOrigin = 'top left';
    clone.style.transform = `scale(${scale})`;
  }
  document.body.appendChild(clone);

  const sourceCanvases = source.querySelectorAll('canvas');
  clone.querySelectorAll('canvas').forEach((canvas, index) => {
    const sourceCanvas = sourceCanvases[index];
    if (!sourceCanvas) return;
    canvas.width = sourceCanvas.width;
    canvas.height = sourceCanvas.height;
    canvas.getContext('2d')?.drawImage(sourceCanvas, 0, 0);
  });

  const sourceVideos = source.querySelectorAll('video');
  clone.querySelectorAll('video').forEach((video, index) => {
    const sourceVideo = sourceVideos[index];
    if (!sourceVideo) return;
    video.currentTime = sourceVideo.currentTime;
    video.pause();
  });

  const cloneRect = clone.getBoundingClientRect();
  const bubbleRect = clone
    .querySelector('.right-bubble, .left-bubble')
    ?.getBoundingClientRect();
  if (bubbleRect) {
    const horizontalOffset = getMobileFocusedMessageHorizontalOffset({
      orientation: sourceBubble?.classList.contains('right-bubble')
        ? 'right'
        : 'left',
      bubbleLeft: bubbleRect.left,
      bubbleRight: bubbleRect.right,
      targetLeft: sourceBubbleRect?.left ?? bubbleRect.left,
      targetRight: sourceBubbleRect?.right ?? bubbleRect.right,
    });
    clone.style.left = fixedPosition(sourceRect.left + horizontalOffset, zoom);
  }
  const focusedBubble = clone.querySelector('.right-bubble, .left-bubble');
  const focusedBubbleRect = focusedBubble?.getBoundingClientRect();
  const focusedMessageCenter = getMobileFocusedMessageCenter({
    bubbleTop: sourceBubbleRect?.top,
    bubbleBottom: sourceBubbleRect?.bottom,
    bubbleHeight: focusedBubbleRect?.height,
    viewportHeight: viewport.height,
    viewportWidth: viewport.width,
    zoom,
  });
  // No álbum mobile, a cópia pode recalcular a grade antes de as miniaturas
  // estabilizarem e produzir um deslocamento vertical maior que o original.
  // A geometria da bolha já renderizada mantém o álbum junto das reações.
  const isImageAlbum = sourceBubble?.dataset.bubbleName === 'image-album';
  let bubbleCenterOffset = bubbleRect
    ? bubbleRect.top - cloneRect.top + bubbleRect.height / 2
    : cloneRect.height / 2;
  if (isImageAlbum && sourceBubbleRect) {
    bubbleCenterOffset =
      (sourceBubbleRect.top - sourceRect.top + sourceBubbleRect.height / 2) *
      scale;
  }
  clone.style.top = fixedPosition(focusedMessageCenter - bubbleCenterOffset, zoom);
  const positionedBubbleRect = focusedBubble?.getBoundingClientRect();
  contextMenuPosition.value = {
    ...contextMenuPosition.value,
    focusedCenter: focusedMessageCenter,
    bubbleHeight: positionedBubbleRect?.height || sourceBubbleRect?.height,
    bubbleLeft: positionedBubbleRect?.left,
    bubbleRight: positionedBubbleRect?.right,
  };
  focusedMessageClone = clone;
}
// Aceleres: reação rápida estilo WhatsApp — smile no hover + pill 6 emojis + '+'.
const showQuickReact = ref(false);
const initialShowPicker = ref(false);
const messageContainerRef = ref(null);
const quickReactRef = ref(null);
// Aceleres: ref do menu de contexto pra reusar o handleForward na seta de forward.
const contextMenuRef = ref(null);
// Estilo inline (coords de viewport) da pill — ela é position:fixed teleported
// pro body, então left/top/bottom são relativos ao viewport, não ao bubble.
const pillStyle = ref({});
const pillDirection = ref('up');
let quickReactOutsideHandler = null;
let quickReactScrollHandler = null;
let quickReactKeyHandler = null;
let quickReactViewportHandler = null;
const { t } = useI18n();
const route = useRoute();
const inboxGetter = useMapGetter('inboxes/getInbox');
const conversationGetter = useMapGetter('getConversationById');
const store = useStore();
const currentAccountIdGetter = useMapGetter('getCurrentAccountId');
const inbox = computed(() => inboxGetter.value(props.inboxId) || {});
const isMessengerMessage = computed(
  () =>
    inbox.value.channel_type === 'Channel::FacebookPage' ||
    props.contentAttributes?.wameProvider === 'messenger' ||
    props.contentAttributes?.wame_provider === 'messenger'
);
const { replaceInstallationName } = useBranding();

/**
 * Computes the message variant based on props
 * @type {import('vue').ComputedRef<'user'|'agent'|'activity'|'private'|'bot'|'template'>}
 */
const variant = computed(() => {
  if (props.private) return MESSAGE_VARIANTS.PRIVATE;

  if (props.isEmailInbox) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) {
      return MESSAGE_VARIANTS.EMAIL;
    }
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return MESSAGE_VARIANTS.EMAIL;
  }

  if (props.status === MESSAGE_STATUS.FAILED) return MESSAGE_VARIANTS.ERROR;
  if (props.contentAttributes?.isUnsupported)
    return MESSAGE_VARIANTS.UNSUPPORTED;

  if (props.contentAttributes?.externalEcho) {
    return MESSAGE_VARIANTS.AGENT;
  }

  const isBot =
    props.sender?.type === SENDER_TYPES.AGENT_BOT ||
    props.senderType === SENDER_TYPES.AGENT_BOT ||
    (!props.sender && !props.additionalAttributes?.senderName);
  if (isBot && props.messageType === MESSAGE_TYPES.OUTGOING) {
    return MESSAGE_VARIANTS.BOT;
  }

  const variants = {
    [MESSAGE_TYPES.INCOMING]: MESSAGE_VARIANTS.USER,
    [MESSAGE_TYPES.ACTIVITY]: MESSAGE_VARIANTS.ACTIVITY,
    [MESSAGE_TYPES.OUTGOING]: MESSAGE_VARIANTS.AGENT,
    [MESSAGE_TYPES.TEMPLATE]: MESSAGE_VARIANTS.TEMPLATE,
  };

  return variants[props.messageType] || MESSAGE_VARIANTS.USER;
});

const isBotOrAgentMessage = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) {
    return false;
  }
  // if an outgoing message is still processing, then it's definitely a
  // message sent by the current user
  if (
    props.status === MESSAGE_STATUS.PROGRESS &&
    props.messageType === MESSAGE_TYPES.OUTGOING
  ) {
    return true;
  }
  const senderId = props.senderId ?? props.sender?.id;
  const senderType = props.sender?.type ?? props.senderType;

  if (!senderType || !senderId) {
    return true;
  }

  if (
    [SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(
      senderType
    )
  ) {
    return true;
  }

  return senderType.toLowerCase() === SENDER_TYPES.USER.toLowerCase();
});

/**
 * Computes the message orientation based on sender type and message type
 * @returns {import('vue').ComputedRef<'left'|'right'|'center'>} The computed orientation
 */
const orientation = computed(() => {
  // Albums are rendered after the batch settles and use persisted message
  // data, whose sender metadata can differ from the temporary message.
  // Keep their direction tied to the logical message type instead.
  if (Array.isArray(props.albumMessages) && props.albumMessages.length >= 2) {
    return props.messageType === MESSAGE_TYPES.OUTGOING
      ? ORIENTATION.RIGHT
      : ORIENTATION.LEFT;
  }

  if (props.messageType === MESSAGE_TYPES.OUTGOING) {
    return ORIENTATION.RIGHT;
  }

  if (isBotOrAgentMessage.value) {
    return ORIENTATION.RIGHT;
  }

  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return ORIENTATION.CENTER;

  return ORIENTATION.LEFT;
});

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const gridClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'grid grid-cols-1fr',
    [ORIENTATION.RIGHT]: 'grid grid-cols-[1fr_24px]',
  };

  return map[orientation.value];
});

const gridTemplate = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: `
      "bubble"
      "reactions"
      "meta"
    `,
    [ORIENTATION.RIGHT]: `
      "bubble avatar"
      "reactions spacer"
      "meta spacer"
    `,
  };

  return map[orientation.value];
});

const shouldGroupWithNext = computed(() => {
  if (props.status === MESSAGE_STATUS.FAILED) return false;

  return props.groupWithNext;
});

const shouldShowAvatar = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return false;
  if (orientation.value === ORIENTATION.LEFT) return false;

  return true;
});

const isPossibleAbsenceAlertMessage = computed(
  () =>
    props.contentAttributes?.aceleresComponent === 'possible_absence_alert' ||
    props.contentAttributes?.aceleres_component === 'possible_absence_alert'
);

const componentToRender = computed(() => {
  // Aceleres: bolha de possivel falta pausada temporariamente.
  // if (isPossibleAbsenceAlertMessage.value) return AceleresPossibleAbsenceAlert;

  // Aceleres: album de imagens/videos ou de stickers tipo WhatsApp — quando
  // MessageList detectou um run de 2+ mensagens consecutivas do mesmo sender
  // e passou o array via `album-messages`, ignoramos os checks abaixo e
  // renderizamos o grid. MessageList só forma runs homogêneos (nunca mistura
  // sticker com foto), então checar a primeira mensagem do run basta.
  if (Array.isArray(props.albumMessages) && props.albumMessages.length >= 2) {
    const firstAlbumAttrs = props.albumMessages[0]?.contentAttributes || {};
    const firstAlbumMediaType = String(
      firstAlbumAttrs.wameMediaType ?? firstAlbumAttrs.wame_media_type ?? ''
    ).toLowerCase();
    return firstAlbumMediaType === 'sticker'
      ? StickerAlbumBubble
      : ImageAlbumBubble;
  }

  if (props.isEmailInbox && !props.private) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) return EmailBubble;
  }

  if (props.contentType === CONTENT_TYPES.INPUT_CSAT) {
    return CSATBubble;
  }

  if (
    [CONTENT_TYPES.INPUT_SELECT, CONTENT_TYPES.FORM].includes(props.contentType)
  ) {
    return FormBubble;
  }

  if (props.contentType === CONTENT_TYPES.VOICE_CALL) {
    return VoiceCallBubble;
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return EmailBubble;
  }

  if (props.contentAttributes?.isUnsupported) {
    return UnsupportedBubble;
  }

  if (props.contentAttributes.type === 'dyte') {
    return DyteBubble;
  }

  const instagramSharedTypes = [
    ATTACHMENT_TYPES.STORY_MENTION,
    ATTACHMENT_TYPES.IG_STORY,
    ATTACHMENT_TYPES.IG_STORY_REPLY,
    ATTACHMENT_TYPES.IG_POST,
  ];
  if (instagramSharedTypes.includes(props.contentAttributes.imageType)) {
    return InstagramStoryBubble;
  }

  if (Array.isArray(props.attachments) && props.attachments.length === 1) {
    const attachment = props.attachments[0];
    const fileType = attachment.fileType;
    const wameMediaType =
      props.contentAttributes?.wameMediaType ??
      props.contentAttributes?.wame_media_type;
    const normalizedWameMediaType = String(wameMediaType || '').toLowerCase();
    const isWameDocument = normalizedWameMediaType === 'document';
    // Aceleres: a decisao de GIF sai do helper unico — assim o MP4 curto e
    // mudo que a WAME entregou sem `wame_media_type` (conversa 670) tambem
    // vira GIF, em vez de bolha de video com controles.
    const isWameGif = isGifMessage({
      content_attributes: props.contentAttributes,
      attachments: props.attachments,
    });
    const isWameSticker =
      props.contentType === CONTENT_TYPES.STICKER ||
      normalizedWameMediaType === 'sticker';
    // O mesmo reconhecimento que a citação e a lista de mensagens usam — uma
    // figurinha vetorial não pode ser vetorial num lugar e não noutro.
    const isLottieSticker = isLottieStickerAttachment(props.attachments[0]);
    const hasOnlyTechnicalMediaContent = [
      'mídia recebida',
      'media received',
    ].includes(
      String(props.content || '')
        .trim()
        .toLowerCase()
    );

    // Áudios enviados como arquivo continuam sendo áudio pela extensão ou pelo
    // MIME, mesmo quando uma integração antiga os marcou como documento.
    // Documents keep their document card even when WhatsApp includes a caption.
    // The caption is rendered inside FileBubble below the file metadata.
    // Aceleres: figurinha vetorial (Lottie) — o anexo e a animacao em JSON,
    // entao ela precisa ser reconhecida antes do cartao de arquivo.
    if (isWameSticker && isLottieSticker) return LottieStickerBubble;
    if (isAudioAttachment(attachment)) return AudioBubble;
    if (isWameDocument || fileType === ATTACHMENT_TYPES.FILE) return FileBubble;
    // GIFs diretos são image/gif. Os GIFs nativos do WhatsApp podem chegar à
    // WAME como MP4 silencioso: preservamos a semântica de GIF, mas precisamos
    // do VideoBubble para o navegador reproduzir o arquivo, sem controles.
    if (isWameGif && fileType === ATTACHMENT_TYPES.VIDEO) return VideoBubble;
    if (isWameGif) return ImageBubble;
    if (isWameSticker) return ImageBubble;
    if (fileType === ATTACHMENT_TYPES.IMAGE) return ImageBubble;
    // Vídeos com legenda continuam no VideoBubble para que a legenda tenha
    // o limite e a expansão controlados pela própria mídia.
    if (fileType === ATTACHMENT_TYPES.VIDEO) return VideoBubble;
    if (fileType === ATTACHMENT_TYPES.IG_REEL) return VideoBubble;
    if (!props.content || hasOnlyTechnicalMediaContent) {
      if (fileType === ATTACHMENT_TYPES.AUDIO) return AudioBubble;
      if (fileType === ATTACHMENT_TYPES.EMBED) return EmbedBubble;
      if (fileType === ATTACHMENT_TYPES.LOCATION) return LocationBubble;
    }
    // Attachment content is the name of the contact
    if (fileType === ATTACHMENT_TYPES.CONTACT) return ContactBubble;
  }

  return TextBubble;
});

const shouldShowContextMenu = computed(() => {
  return !props.contentAttributes?.isUnsupported;
});

const isBubble = computed(() => {
  return props.messageType !== MESSAGE_TYPES.ACTIVITY;
});
const isTextOnlyMessage = computed(
  () => componentToRender.value === TextBubble && !props.attachments?.length
);
const isPrivateTextNote = computed(
  () => isTextOnlyMessage.value && variant.value === MESSAGE_VARIANTS.PRIVATE
);
const hasImageCaption = computed(() => {
  if (componentToRender.value !== ImageBubble) return false;

  const content = String(props.content || '').trim();
  return (
    Boolean(content) &&
    !['mídia recebida', 'media received'].includes(content.toLowerCase())
  );
});

// Aceleres: "álbum" aqui significa "essa bolha representa 2+ mensagens
// agrupadas visualmente" — vale tanto pro grid de fotos/vídeos quanto pro
// par de stickers. Reaproveitado por copy/replyTo/addToNote (não fazem
// sentido apontando pra várias mensagens de uma vez) e pela seleção múltipla
// abaixo, sem duplicar esse conjunto de regras pros dois tipos de álbum.
const isAlbumBubble = computed(() =>
  [ImageAlbumBubble, StickerAlbumBubble].includes(componentToRender.value)
);
const isStickerAlbumBubble = computed(
  () => componentToRender.value === StickerAlbumBubble
);
const isStickerBubble = computed(() => {
  if (componentToRender.value === LottieStickerBubble) return true;

  const attributes = props.contentAttributes || {};
  const mediaType =
    attributes.wameMediaType ?? attributes.wame_media_type ?? '';

  return (
    componentToRender.value === ImageBubble &&
    (props.contentType === CONTENT_TYPES.STICKER ||
      String(mediaType).toLowerCase() === 'sticker')
  );
});
const hasMessageReactions = computed(() =>
  Boolean(props.contentAttributes?.reactions?.length)
);
const rendersExternalReactions = computed(
  () => !isStickerAlbumBubble.value && hasMessageReactions.value
);

// Aceleres: GIF chega como VideoBubble (mp4 mudo), mas pro botão de
// encaminhar ele conta como GIF, não como vídeo — só vídeo de verdade fica
// de fora (pedido 2026-08-11).
const isNonGifVideoBubble = computed(() => {
  const attributes = props.contentAttributes || {};
  const mediaType =
    attributes.wameMediaType ?? attributes.wame_media_type ?? '';

  return (
    componentToRender.value === VideoBubble &&
    String(mediaType).toLowerCase() !== 'gif'
  );
});
// Aceleres: botão de encaminhar também sai do áudio (pedido 2026-08-11).
const isAudioBubble = computed(() => componentToRender.value === AudioBubble);
const isForwardMediaBubble = computed(() =>
  [ImageBubble, ImageAlbumBubble, VideoBubble].includes(componentToRender.value)
);
const isMobileForwardBubble = computed(
  () =>
    isForwardMediaBubble.value || isStickerBubble.value || isAudioBubble.value
);
// Aceleres: mesma condição do v-if do botão de encaminhar (ver template,
// perto de "aceleres-bubble-forward"), extraída pra computed porque o
// smile também precisa saber se o forward existe ao lado dele — sem
// forward, a margem de 46px do smile (reservada pra caber o forward
// colado nele) sobra, deixando um vão vazio grande demais até a bolha.
const shouldShowForwardButton = computed(() => {
  const canShowForward =
    shouldShowContextMenu.value &&
    isBubble.value &&
    !isStickerAlbumBubble.value &&
    !isTextOnlyMessage.value;

  if (!canShowForward) return false;

  // No telemóvel, o encaminhamento fica sempre disponível nas mídias que o
  // utilizador reconhece visualmente.
  if (isMobileViewport.value) return isMobileForwardBubble.value;

  return (
    !isStickerBubble.value && !isAudioBubble.value && !isNonGifVideoBubble.value
  );
});
// Aceleres: StickerAlbumBubble fica de fora — ela tem sua PRÓPRIA flechinha
// por sticker (ver StickerAlbum.vue), então a flechinha genérica única do
// Message.vue é suprimida pra ela (ver `!isStickerAlbumBubble` no botão
// abaixo). Mantê-la aqui seria estado morto, sem efeito nenhum.
const isMediaChevronBubble = computed(() =>
  [ImageBubble, ImageAlbumBubble, FileBubble, VideoBubble].includes(
    componentToRender.value
  )
);
const hasAdaptiveMediaMenuSlot = computed(
  () =>
    componentToRender.value === ImageBubble ||
    componentToRender.value === VideoBubble
);
const chevronStyleClasses = computed(() => {
  const receivedMediaChevronClass =
    isMediaChevronBubble.value && orientation.value === ORIENTATION.LEFT
      ? 'acl-received-media-chevron'
      : null;

  if (isStickerBubble.value) {
    return [
      '!top-1.5',
      '!right-1.5',
      '!h-7',
      '!w-7',
      '!rounded-full',
      '!bg-n-slate-3',
      '!text-n-slate-11',
      'shadow-sm',
      receivedMediaChevronClass,
    ];
  }

  if (isMediaChevronBubble.value) {
    return [
      '!top-2',
      '!right-2',
      '!bg-transparent',
      '!text-n-slate-11',
      receivedMediaChevronClass,
    ];
  }

  return ['!top-1.5', '!right-2', '!bg-transparent', '!text-n-slate-11'];
});
const adaptiveChevronToneClasses = tone => {
  if (isStickerBubble.value) return [];

  // Nas mensagens enviadas, a seta pertence ao controle da bolha e permanece
  // preta independentemente do tom da imagem. A adaptacao continua exclusiva
  // da midia recebida, onde o fundo pode exigir contraste branco.
  if (orientation.value === ORIENTATION.RIGHT) {
    return ['!text-black', 'drop-shadow-sm'];
  }

  return tone === 'dark'
    ? ['!text-white', 'drop-shadow-md']
    : ['!text-n-slate-11', 'drop-shadow-sm'];
};
// Aceleres: nome ficou de quando so cobria imagem — agora tambem video e
// documento (isMediaChevronBubble), pra encaminhar/reagir ancorarem certo
// nesses tipos tambem. Mantido sem renomear: `data-outgoing-image` e
// `.acl-outgoing-image-wrapper` sao consumidos por 3 arquivos CSS
// (aceleres-overrides.css, aceleres-desktop-media-fix.css) e as regras la
// sao so de layout (largura/alinhamento), nada especifico de imagem — so as
// que TAMBEM exigem `[data-bubble-name='image']` continuam so-imagem.
const isOutgoingImageMessage = computed(
  () =>
    isMediaChevronBubble.value && props.messageType === MESSAGE_TYPES.OUTGOING
);

const isMessageDeleted = computed(() => {
  return props.contentAttributes?.deleted;
});

const isWameMediaPending = computed(() => {
  // Uma finalizacao pode anexar o arquivo antes de persistir o estagio
  // complete (legado ou resposta interrompida). Com anexo presente, a
  // midia ja esta disponivel e o placeholder nao pode encobri-la.
  if (Array.isArray(props.attachments) && props.attachments.length > 0) {
    return false;
  }

  const attrs = props.contentAttributes || {};
  const stage = String(
    attrs.wameMediaStage ?? attrs.wame_media_stage ?? ''
  ).toLowerCase();

  return (
    (attrs.wameMediaPending ?? attrs.wame_media_pending) === true ||
    // Aceleres: `failed` entra aqui porque WameMediaProgress e o UNICO
    // componente que sabe desenhar uma midia sem anexo. Sem isto a mensagem
    // caia no componente do tipo (imagem/video/documento) sem arquivo nenhum
    // e a bolha ficava vazia — o "midia recebida sem conteudo" relatado.
    ['bank', 'chatwoot', 'failed'].includes(stage)
  );
});

const payloadForContextMenu = computed(() => {
  // Mobile: o toque longo escolheu UMA figurinha do par — o menu age nela.
  const sticker = focusedStickerMessage.value;
  if (sticker) {
    return {
      id: sticker.id,
      content_attributes: sticker.contentAttributes,
      content: sticker.content,
      conversation_id: sticker.conversationId ?? props.conversationId,
      attachments: sticker.attachments,
      status: sticker.status,
      created_at: sticker.createdAt,
      source_id: sticker.sourceId,
      message_type: sticker.messageType,
      sender: sticker.sender,
      sender_id: sticker.senderId,
      sender_type: sticker.senderType,
    };
  }

  return {
    id: props.id,
    content_attributes: props.contentAttributes,
    content: props.content,
    conversation_id: props.conversationId,
    attachments: props.attachments,
    status: props.status,
    created_at: props.createdAt,
    source_id: props.sourceId,
    message_type: props.messageType,
    // sender é necessário no painel de informações da mensagem (avatar do
    // áudio recebido). Sem ele, só o caso outgoing achava avatar, via
    // conversation.meta.assignee.
    sender: props.sender,
    sender_id: props.senderId,
    sender_type: props.senderType,
  };
});

const contextMenuSelectionMessages = computed(() => {
  // Com uma figurinha escolhida no toque longo, encaminhar leva so ela.
  if (focusedStickerMessage.value) return [];
  if (!isAlbumBubble.value || !Array.isArray(props.albumMessages)) return [];

  return props.albumMessages.map(messageSelectionPayloadFor);
});

const contextMenuEnabledOptions = computed(() => {
  const hasText = !!props.content;
  const hasAttachments = !!(props.attachments && props.attachments.length > 0);
  const firstAttachmentType = hasAttachments
    ? props.attachments[0].fileType
    : null;
  const isAudio = firstAttachmentType === 'audio';
  const isImage = firstAttachmentType === 'image';

  const isOutgoing = props.messageType === MESSAGE_TYPES.OUTGOING;
  const isFailedMessage = props.status === MESSAGE_STATUS.FAILED;
  const isFailedOrProcessing =
    isFailedMessage || props.status === MESSAGE_STATUS.PROGRESS;

  return {
    // Aceleres: itens alinhados ao popup do WhatsApp.
    info: !isFailedOrProcessing && !isMessageDeleted.value,
    react:
      !isMessengerMessage.value &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value,
    download:
      hasAttachments &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value &&
      !isStickerBubble.value &&
      !isStickerAlbumBubble.value,
    attachmentType: firstAttachmentType,
    copy: hasText && !isAudio && !isAlbumBubble.value,
    edit: false,
    delete: false,
    cannedResponse: isOutgoing && hasText && !isMessageDeleted.value,
    copyLink: !isFailedOrProcessing,
    translate: !isFailedOrProcessing && !isMessageDeleted.value && hasText,
    replyTo:
      !isAlbumBubble.value &&
      !props.private &&
      props.inboxSupportsReplyTo.outgoing &&
      !isFailedOrProcessing,
    forward: !isFailedOrProcessing && !isMessageDeleted.value,
    star: !isFailedOrProcessing && !isMessageDeleted.value,
    addToNote:
      !isAlbumBubble.value &&
      hasText &&
      !isAudio &&
      !isImage &&
      !isMessageDeleted.value,
    pinned: isMessagePinned(
      conversationGetter.value(props.conversationId),
      props.id
    ),
  };
});

const shouldRenderMessage = computed(() => {
  // Aceleres: esconde mensagens ja criadas do alerta de possivel falta enquanto
  // a funcionalidade esta pausada para o publico.
  if (isPossibleAbsenceAlertMessage.value) return false;

  const hasAttachments = !!(props.attachments && props.attachments.length > 0);
  const isEmailContentType = props.contentType === CONTENT_TYPES.INCOMING_EMAIL;
  const isUnsupported = props.contentAttributes?.isUnsupported;
  const isAnIntegrationMessage =
    props.contentType === CONTENT_TYPES.INTEGRATIONS;
  const isFailedMessage = props.status === MESSAGE_STATUS.FAILED;
  const hasExternalError = !!props.contentAttributes?.externalError;

  return (
    hasAttachments ||
    props.content ||
    isEmailContentType ||
    isUnsupported ||
    isAnIntegrationMessage ||
    isFailedMessage ||
    hasExternalError
  );
});

// Aceleres: ancora a pilha de ações e reações na própria bolha, não no ponto
// do clique. Assim as duas partes permanecem alinhadas em mensagens curtas,
// longas, recebidas e enviadas.
// (findBubble, findContextMenuTrigger, getDashboardZoom, getVisualViewportSize,
// fixedPosition e computeMenuAnchor vêm de contextMenuAnchor.js — StickerAlbum.vue
// usa as mesmas pro menu de cada sticker do par.)

function getQuickReactViewportSize() {
  const zoom = getDashboardZoom();
  const rootRect = document.documentElement.getBoundingClientRect();

  return {
    zoom,
    // Aceleres 2026-08-25: o fallback multiplicava a viewport pelo zoom. Ela ja
    // vem em espaco visual, tal como o `rootRect` — ver helper/dashboardZoom.js.
    width: rootRect.width || getVisualViewport().width,
    height: rootRect.height || getVisualViewport().height,
  };
}

function announceMessageInteraction(interaction) {
  emitter.emit(BUS_EVENTS.MESSAGE_INTERACTION_OPENED, {
    messageId: props.id,
    interaction,
  });
}

function openContextMenu(e, explicitAnchor = null) {
  // Aceleres: em click direito (e long-press) abrimos o popup mesmo em <img>
  // e <a> — usuário pediu pra que reaja em imagens também. Só pulamos quando
  // a UI explicitamente marca skip-context-menu (botões internos da bolha).
  const isMobileContextGesture = isMobileMessageMenu();
  if (isMobileContextGesture) e.preventDefault();

  const shouldSkipContextMenu =
    isSelectionMode.value ||
    e.target?.classList.contains('skip-context-menu') ||
    !!getSelection().toString();
  if (shouldSkipContextMenu) return;
  e.preventDefault();
  if (e.type === 'contextmenu') {
    useTrack(ACCOUNT_EVENTS.OPEN_MESSAGE_CONTEXT_MENU);
  }
  announceMessageInteraction('context-menu');
  focusedStickerTileId.value = resolveStickerTileId(e);
  contextMenuPosition.value = computeMenuAnchor(e, {
    anchor: explicitAnchor || findContextMenuTrigger(e),
    orientation: orientation.value,
    actionsAbove: hasImageCaption.value,
    pointerAnchor: e.type === 'contextmenu',
  });
  showFocusedMessageClone(e);
  showContextMenu.value = true;
}

function preventMobileMediaDrag(e) {
  if (!isMobileViewport.value) return;
  if (
    !e.target?.closest?.(
      "[data-bubble-name='image'], [data-bubble-name='image-album']"
    )
  )
    return;

  e.preventDefault();
}

function handleReaction(emoji) {
  if (isMessengerMessage.value) return;
  const messageId = focusedStickerMessage.value?.id ?? props.id;
  store.dispatch('reactToMessage', {
    accountId: currentAccountIdGetter.value,
    conversationId: props.conversationId,
    messageId,
    currentUserId: props.currentUserId,
    currentUserName: store.getters.getCurrentUser?.name,
    previousReactions:
      messageId === props.id ? props.contentAttributes?.reactions || [] : null,
    emoji,
  });
}

// Resposta da folha de emojis do compositor, aberta pelo "+" da barra rapida
// (ver `openFullPickerFromPill`). O evento chega a TODAS as mensagens da lista,
// entao a comparacao com o proprio id e o que faz so a certa reagir.
function handleReactionEmojiSelected({ messageId, emoji } = {}) {
  if (Number(messageId) !== Number(props.id) || !emoji) return;
  handleReaction(emoji);
}

// Aceleres: modal de detalhe das reacoes (quem reagiu, trocar e remover a
// propria). Abre ancorado na chip de reacao, como no WhatsApp. O card NAO
// fecha ao reagir: a chip da propria reacao alterna entre 1 e 0 ali mesmo.
const showReactionsModal = ref(false);
// Guarda o ELEMENTO da pilula, não um retângulo copiado: o card mede a
// âncora quando vai desenhar e sempre que a página rola, em vez de ficar
// preso à posição do instante do clique. shallowRef porque é um nó do DOM —
// não há nada aqui que valha a pena tornar reativo em profundidade.
const reactionsAnchorEl = shallowRef(null);
function openReactionsModal(el) {
  reactionsAnchorEl.value = el ?? null;
  showReactionsModal.value = true;
}
function closeContextMenu() {
  removeFocusedMessageClone();
  focusedStickerTileId.value = null;
  if (!showContextMenu.value && !initialShowPicker.value) return;
  const shouldCloseQuickReact = initialShowPicker.value;
  showContextMenu.value = false;
  contextMenuPosition.value = { x: null, y: null };
  initialShowPicker.value = false;
  if (shouldCloseQuickReact) closeQuickReact();
}

// O atalho ao lado da bolha abre diretamente o seletor de destinos. Para um
// álbum, o ContextMenu recebe todas as mensagens e o encaminhamento mantém o
// grupo inteiro, como no WhatsApp.
function forwardThisMessage() {
  contextMenuRef.value?.openForwardModal?.();
}

// Aceleres: pill de reação rápida (WhatsApp-like). A pill é renderizada via
// <Teleport to="body"> + position:fixed — MESMO padrão do emoji-picker
// (MessageContextMenu). Motivo: a pill abre PARA CIMA nos outgoing (is-right)
// e era clipada pelo overflow-y-auto do .conversation-panel quando ultrapassava
// a borda superior do container de scroll, ficando "metade cortada". Sendo
// fixed no body, ela escapa de qualquer overflow/stacking e fica por cima de
// tudo. Coords calculadas a partir do rect do botão smile; fecha no scroll
// (a pill é fixed, não acompanha a mensagem ao rolar).
function closeQuickReact() {
  // O picker completo mantém a barra rápida como sua âncora visual. Nenhum
  // handler secundário pode desmontá-la enquanto ele estiver aberto.
  if (initialShowPicker.value) return;

  if (
    !showQuickReact.value &&
    !quickReactOutsideHandler &&
    !quickReactScrollHandler &&
    !quickReactKeyHandler &&
    !quickReactViewportHandler
  ) {
    return;
  }
  showQuickReact.value = false;
  pillDirection.value = 'up';
  if (quickReactOutsideHandler) {
    document.removeEventListener('pointerdown', quickReactOutsideHandler, true);
    quickReactOutsideHandler = null;
  }
  if (quickReactScrollHandler) {
    document.removeEventListener('scroll', quickReactScrollHandler, true);
    document.removeEventListener('touchmove', quickReactScrollHandler, true);
    quickReactScrollHandler = null;
  }
  if (quickReactKeyHandler) {
    document.removeEventListener('keydown', quickReactKeyHandler);
    quickReactKeyHandler = null;
  }
  if (quickReactViewportHandler) {
    window.removeEventListener('resize', quickReactViewportHandler);
    window.visualViewport?.removeEventListener(
      'resize',
      quickReactViewportHandler
    );
    window.visualViewport?.removeEventListener(
      'scroll',
      quickReactViewportHandler
    );
    quickReactViewportHandler = null;
  }
}

function handleMessageInteractionOpened({ messageId, interaction } = {}) {
  const isCurrentMessage = Number(messageId) === Number(props.id);
  const isQuickPicker = interaction === 'quick-reaction-picker';
  if (!isCurrentMessage || (interaction !== 'context-menu' && !isQuickPicker)) {
    closeContextMenu();
  }
  if (
    !isCurrentMessage ||
    (interaction !== 'quick-reaction' && !isQuickPicker)
  ) {
    closeQuickReact();
  }
}
// Aceleres: posiciona a pill junto ao botão smile e evita cobrir mensagens
// somente de texto quando a pill não couber acima da bolha.
function positionQuickReactPill(smile) {
  const root = quickReactRef.value;
  if (!root || !smile) return;

  const rect = smile.getBoundingClientRect();
  const {
    zoom: pageZoom,
    width: viewportWidth,
    height: viewportHeight,
  } = getQuickReactViewportSize();
  const pillW = (root.offsetWidth || 280) * pageZoom;
  const pillH = (root.offsetHeight || 48) * pageZoom;
  const GAP = 4;
  const PAD = 8;
  const anchorTop = rect.top;
  const anchorCx = rect.left + rect.width / 2;
  const visualTop = Math.min(
    Math.max(anchorTop - pillH - GAP, PAD),
    Math.max(PAD, viewportHeight - pillH - PAD)
  );
  let adjustedTop = visualTop;
  let opensAbove = true;

  if (isTextOnlyMessage.value) {
    const bubble = smile
      .closest?.('.aceleres-bubble-with-actions')
      ?.querySelector?.('.aceleres-bubble-surface');
    const bubbleRect = bubble?.getBoundingClientRect?.();

    // Aceleres 2026-08-18: subir a pill para cima do balao INTEIRO so faz
    // sentido enquanto o balao e baixo — ai a pill colada ao smile taparia a
    // mensagem toda. Num balao alto o smile fica no meio dele, e essa subida
    // atirava a pill para dezenas (ou centenas) de pixeis acima do botao que a
    // abriu: a barra aparecia solta no topo da mensagem, longe do cursor. Num
    // balao alto ela fica colada ao smile — como a pill e centrada no smile, e
    // o smile vive FORA do balao, o que ela tapa e meia linha na lateral.
    const bubbleIsShort = bubbleRect ? bubbleRect.height <= pillH * 2 : false;

    if (bubbleIsShort && visualTop + pillH > bubbleRect.top) {
      const fitsAbove = bubbleRect.top >= pillH + GAP + PAD;
      const fitsBelow = viewportHeight - bubbleRect.bottom >= pillH + GAP + PAD;

      if (fitsAbove) {
        adjustedTop = bubbleRect.top - pillH - GAP;
      } else if (fitsBelow) {
        adjustedTop = bubbleRect.bottom + GAP;
        opensAbove = false;
      } else if (bubbleRect.top >= viewportHeight - bubbleRect.bottom) {
        adjustedTop = Math.max(bubbleRect.top - pillH - GAP, PAD);
      } else {
        adjustedTop = Math.min(
          bubbleRect.bottom + GAP,
          viewportHeight - pillH - PAD
        );
        opensAbove = false;
      }
    }
  }

  const visualCenterX = Math.min(
    Math.max(anchorCx, PAD + pillW / 2),
    viewportWidth - PAD - pillW / 2
  );

  pillStyle.value = {
    left: fixedPosition(visualCenterX, pageZoom),
    top: fixedPosition(adjustedTop, pageZoom),
    bottom: '',
    visibility: 'visible',
  };
  pillDirection.value = opensAbove ? 'up' : 'down';
}

function installQuickReactListeners() {
  // O listener entra no próximo ciclo para não capturar o toque/click de
  // abertura da própria faixa de reações.
  setTimeout(() => {
    if (!showQuickReact.value) return;
    quickReactOutsideHandler = event => {
      const root = quickReactRef.value;
      // Enquanto o picker completo estiver aberto, ele próprio controla o
      // click-outside. Assim, as abas de categoria não fecham a barra rápida.
      if (initialShowPicker.value) return;
      if (root && !root.contains(event.target)) {
        closeQuickReact();
      }
    };
    document.addEventListener('pointerdown', quickReactOutsideHandler, true);
  }, 0);

  quickReactScrollHandler = () => closeQuickReact();
  document.addEventListener('scroll', quickReactScrollHandler, true);
  document.addEventListener('touchmove', quickReactScrollHandler, {
    capture: true,
    passive: true,
  });
  quickReactKeyHandler = event => {
    if (event.key === 'Escape') closeQuickReact();
  };
  document.addEventListener('keydown', quickReactKeyHandler);
  quickReactViewportHandler = () => closeQuickReact();
  window.addEventListener('resize', quickReactViewportHandler, {
    passive: true,
  });
  window.visualViewport?.addEventListener('resize', quickReactViewportHandler, {
    passive: true,
  });
  window.visualViewport?.addEventListener('scroll', quickReactViewportHandler, {
    passive: true,
  });
}

function openQuickReactAtAnchor(anchor) {
  if (isMessengerMessage.value) return;
  announceMessageInteraction('quick-reaction');
  pillStyle.value = { ...pillStyle.value, visibility: 'hidden' };
  showQuickReact.value = true;
  nextTick(() => positionQuickReactPill(anchor));
  installQuickReactListeners();
}

function toggleQuickReact(e) {
  e?.stopPropagation?.();
  if (showQuickReact.value) {
    closeQuickReact();
    return;
  }
  // Âncora no botão smile que disparou o clique. Robusto: sobe até o
  // message-bubble-container e querySelector o smile de lá (currentTarget
  // pode estar resetado fora do dispatch).
  const triggerBtn = e?.currentTarget || e?.target;
  const container =
    triggerBtn?.closest?.('.message-bubble-container') ||
    e?.target?.closest?.('.message-bubble-container');
  const smile =
    container?.querySelector?.('.aceleres-bubble-smile') || triggerBtn;
  openQuickReactAtAnchor(smile);
}
function openQuickReactFromContextMenu() {
  const smile = messageContainerRef.value?.querySelector(
    '.aceleres-bubble-smile'
  );
  if (!smile) return;

  closeContextMenu();
  nextTick(() => openQuickReactAtAnchor(smile));
}
function handleQuickReact(emoji) {
  handleReaction(emoji);
  closeQuickReactAndPicker();
  if (
    isSelectionMode.value &&
    isMobileMessageMenu()
  ) {
    exitSelectionMode();
  }
}
function closeQuickReactAndPicker() {
  if (initialShowPicker.value) {
    closeContextMenu();
    return;
  }
  closeQuickReact();
}
function openFullPickerFromPill(e) {
  // Aceleres 2026-09-15: no telemovel o "+" abre a folha grande de emojis
  // (MobileEmojiSheet), a mesma de todos os "+" de reacao rapida.
  //
  // Aqui so se pede; quem abre e o ReplyBox, que e dono dela. A resposta volta
  // pelo `REACTION_EMOJI_SELECTED`, tratado no `onMounted` — e por isso que
  // este componente fecha tudo e nao fica a espera de nada.
  if (isMobileMessageMenu()) {
    closeQuickReact();
    closeContextMenu();
    emitter.emit(BUS_EVENTS.OPEN_REACTION_EMOJI_SHEET, { messageId: props.id });
    return;
  }

  announceMessageInteraction('quick-reaction-picker');
  initialShowPicker.value = true;
  const quickPill = quickReactRef.value;
  if (quickPill) {
    contextMenuPosition.value = computePickerAnchorFromRect(
      quickPill.getBoundingClientRect()
    );
    showContextMenu.value = true;
    return;
  }

  // Fallback para o botão smile quando a barra de reações não estiver
  // disponível no momento do clique.
  const container =
    e?.target?.closest?.('.message-bubble-container') ||
    e?.currentTarget?.closest?.('.message-bubble-container');
  const smile = container?.querySelector?.('.aceleres-bubble-smile');
  if (smile) {
    contextMenuPosition.value = computePickerAnchorFromRect(
      smile.getBoundingClientRect()
    );
    showContextMenu.value = true;
    return;
  }
  openContextMenu(e);
}

// Aceleres: posiciona o emoji-picker-card em relação ao elemento que abriu o
// seletor, mantendo-o centralizado e dentro da área visível da conversa.
function computePickerAnchorFromRect(rect) {
  // A medida compacta declarada no seletor da mensagem e 390x320. Com
  // `html { zoom: .8 }` o cartao ocupa 312x256 no ecra, que e a escala de
  // `rect` e de `window.innerWidth/innerHeight` usados nesta conta. Sem
  // `layoutToVisual` reservava-se espaco a mais e o seletor era empurrado para
  // longe do botao. Ver helper/dashboardZoom.js.
  const zoom = getDashboardZoom();
  const PICKER_W = layoutToVisual(390, zoom);
  // A altura acompanha a versao compacta; o popup mede o tamanho renderizado
  // depois e ajusta a posicao final dentro da area visivel.
  const PICKER_H = layoutToVisual(320, zoom);
  const OFFSET = 16;
  const PAD = 8;

  const messagesArea = document.querySelector(
    '.flex.flex-col.justify-between.flex-grow.h-full.min-w-0.m-0.scrollbar-thin'
  );
  const areaRect = messagesArea ? messagesArea.getBoundingClientRect() : null;
  const areaTop = areaRect ? areaRect.top : 0;
  const areaBottom = areaRect ? areaRect.bottom : window.innerHeight;

  const smileTop = rect.top;
  const smileBottom = rect.bottom;
  const smileCenterX = rect.left + rect.width / 2;
  const spaceAbove = smileTop - areaTop;
  const spaceBelow = areaBottom - smileBottom;
  const needed = PICKER_H + OFFSET + PAD;

  let topPx = '';
  let bottomPx = '';
  let direction;
  if (spaceAbove >= needed) {
    direction = 'up';
    bottomPx = fixedPosition(window.innerHeight - smileTop + OFFSET, zoom);
  } else if (spaceBelow >= needed) {
    direction = 'down';
    topPx = fixedPosition(smileBottom + OFFSET, zoom);
  } else if (spaceBelow >= spaceAbove) {
    direction = 'down';
    topPx = fixedPosition(smileBottom + OFFSET, zoom);
  } else {
    direction = 'up';
    bottomPx = fixedPosition(window.innerHeight - smileTop + OFFSET, zoom);
  }

  let leftEdge = smileCenterX - PICKER_W / 2;
  if (leftEdge < PAD) leftEdge = PAD;
  const maxLeft = window.innerWidth - PAD - PICKER_W;
  if (leftEdge > maxLeft) leftEdge = Math.max(PAD, maxLeft);

  const originY = direction === 'down' ? 'top' : 'bottom';
  return {
    // O cartao e `position: fixed` dentro do `html` com zoom: o valor escrito
    // em CSS tem de voltar ao espaco de layout.
    left: fixedPosition(leftEdge, zoom),
    right: '',
    top: topPx,
    bottom: bottomPx,
    direction,
    transformOrigin: originY + ' center',
    anchorLeft: rect.left,
    anchorTop: rect.top,
    anchorRight: rect.right,
    anchorBottom: rect.bottom,
    anchorWidth: rect.width,
    anchorHeight: rect.height,
    anchorCenter: smileCenterX,
  };
}

let longPressTimer = null;
let touchStartPoint = null;
let longPressTriggered = false;
const LONG_PRESS_DELAY = 450;
const LONG_PRESS_MOVE_TOLERANCE = 12;

function cancelLongPress() {
  if (longPressTimer) clearTimeout(longPressTimer);
  longPressTimer = null;
  touchStartPoint = null;
}

function onTouchStart(e) {
  // Durante a seleção múltipla, o toque simples adiciona ou remove mensagens.
  // A pressão longa só abre o menu quando ainda não existe seleção ativa.
  if (isSelectionMode.value) return;
  // Um novo toque ja e outro gesto e deve funcionar normalmente, mesmo que o
  // navegador anterior nao tenha emitido o clique de compatibilidade.
  clearSuppressedClick();
  cancelLongPress();
  longPressTriggered = false;
  const touch = e.touches?.[0];
  if (!touch) return;

  touchStartPoint = {
    clientX: touch.clientX,
    clientY: touch.clientY,
    target: touch.target || e.target,
    currentTarget: e.currentTarget,
  };
  longPressTimer = setTimeout(() => {
    const point = touchStartPoint;
    if (!point) return;

    const isMobile = isMobileMessageMenu();
    if (isMobile) {
      closeContextMenu();
      getSelection()?.removeAllRanges();
    }

    announceMessageInteraction('context-menu');
    focusedStickerTileId.value = resolveStickerTileId({
      target: point.target,
      currentTarget: point.currentTarget,
    });
    contextMenuPosition.value = computeMenuAnchor(
      {
        target: point.target,
        currentTarget: point.currentTarget,
        clientX: point.clientX,
        clientY: point.clientY,
      },
      {
        orientation: orientation.value,
        actionsAbove: hasImageCaption.value,
      }
    );
    showFocusedMessageClone({
      target: point.target,
      currentTarget: point.currentTarget,
    });
    showContextMenu.value = true;

    if (isMobile) {
      armSuppressedClick();
      longPressTriggered = true;
    }

    longPressTimer = null;
    window.navigator?.vibrate?.(8);
  }, LONG_PRESS_DELAY);
}

function onTouchMove(e) {
  if (!longPressTimer || !touchStartPoint) return;
  const touch = e.touches?.[0];
  if (!touch) return;
  const deltaX = touch.clientX - touchStartPoint.clientX;
  const deltaY = touch.clientY - touchStartPoint.clientY;
  if (Math.hypot(deltaX, deltaY) > LONG_PRESS_MOVE_TOLERANCE) {
    cancelLongPress();
  }
}

function onTouchEnd() {
  cancelLongPress();
  if (!longPressTriggered) return;
  longPressTriggered = false;
  if (suppressNextClickTimer) clearTimeout(suppressNextClickTimer);
  suppressNextClickTimer = setTimeout(() => {
    clearSuppressedClick();
  }, 700);
}

function handleReplyTo() {
  const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
  const { conversationId } = props;
  const sticker = focusedStickerMessage.value;
  const replyTo = sticker?.id ?? props.id;

  LocalStorage.updateJsonStore(replyStorageKey, conversationId, replyTo);
  emitter.emit(
    BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE,
    sticker
      ? {
          ...props,
          id: sticker.id,
          content: sticker.content,
          attachments: sticker.attachments,
          contentAttributes: sticker.contentAttributes,
        }
      : props
  );
}

const avatarInfo = computed(() => {
  const aiDisplayName = aiReplyDisplayName({
    contentAttributes: props.contentAttributes,
    senderType: props.sender?.type ?? props.senderType,
    senderName: props.sender?.name,
  });
  if (aiDisplayName) return { name: aiDisplayName, src: '' };
  if (props.contentAttributes?.externalEcho) {
    const { name, avatar_url, channel_type, medium } = inbox.value;
    const iconName = avatar_url
      ? null
      : getInboxIconByType(channel_type, medium);
    return {
      name: iconName ? '' : name || t('CONVERSATION.NATIVE_APP'),
      src: avatar_url || '',
      iconName,
    };
  }

  // If no sender, check for Slack (or other integration) sender info
  if (!props.sender) {
    const { senderName, senderAvatarUrl } = props.additionalAttributes || {};
    if (senderName) {
      return { name: senderName, src: senderAvatarUrl ?? '' };
    }
    return { name: t('CONVERSATION.BOT'), src: '' };
  }

  const { sender } = props;
  const { name, type, avatarUrl, thumbnail, additionalAttributes } =
    sender || {};
  const customName = additionalAttributes?.aceleresContactName;

  // If sender type is agent bot, use avatarUrl
  if ([SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(type)) {
    return {
      name: name ?? '',
      src: avatarUrl ?? '',
    };
  }

  // Aceleres: Channel::Api costumava exibir a Integracao Aceleres como sender
  // em toda mensagem sincronizada via WAME. O backend passou a resolver o
  // autor real (sender_id) via content_attributes.wame_author_chatwoot_user_id
  // (ver Messages::MessageBuilder#wame_author_sender): quando ele existe, quem
  // assina e quem escreveu, e nada aqui pode reescrever isso.
  // type vem da API como "user" (lowercase) enquanto SENDER_TYPES.USER === "User";
  // o proprio arquivo ja faz toLowerCase() em outro check (isUser computed).
  //
  // A DECISAO de confiar ou nao no sender mora em isUnattributedWameMessage,
  // compartilhada com o balao (Base.vue), com o album de stickers e com o
  // player de audio. Ela estava copiada em cada um deles, e a copia do album
  // divergiu: lia o sender cru e trocava o rotulo do par de figurinhas do nome
  // da instancia para o dono do token admin no instante em que as mensagens
  // eram agrupadas. A APRESENTACAO continua sendo de cada componente — aqui
  // embaixo ela devolve tambem o avatar.
  if (
    inbox.value.channel_type === 'Channel::Api' &&
    type?.toLowerCase?.() === SENDER_TYPES.USER.toLowerCase()
  ) {
    if (
      !isUnattributedWameMessage({
        contentAttributes: props.contentAttributes,
        senderType: type,
        inbox: inbox.value,
      })
    ) {
      return { name: name ?? '', src: thumbnail ?? '' };
    }
    // Sem autor identificavel: a mensagem saiu do celular (ou de outro
    // sistema) no mesmo numero, e nenhum atendente do painel a enviou.
    //
    // Ate 31/08/2026 caia no NOME DO INBOX — o que punha a INSTANCIA no lugar
    // do atendente e fazia a conversa alternar entre o nome de quem enviou
    // pelo painel e o nome do canal. Desde 04/09/2026 quem assina e o
    // ATENDENTE RESPONSAVEL PELO LEAD, com a foto dele, igual ao balao
    // (useDisplaySenderName). O rotulo neutro sobrou so para a conversa sem
    // responsavel; o nome do canal nao volta em hipotese alguma.
    const attendant = conversationAttendantIdentity(
      conversationGetter.value(props.conversationId)
    );
    if (attendant) return attendant;

    return { name: 'Atendente', src: '' };
  }

  // For all other senders, use thumbnail
  return {
    name: customName || name || '',
    src: thumbnail ?? '',
  };
});

const avatarTooltip = computed(() => {
  if (props.contentAttributes?.externalEcho) {
    return replaceInstallationName(t('CONVERSATION.NATIVE_APP_ADVISORY'));
  }
  if (avatarInfo.value.name === '') return '';
  return `${t('CONVERSATION.SENT_BY')} ${avatarInfo.value.name}`;
});

const setupHighlightTimer = () => {
  if (Number(route.query.messageId) !== Number(props.id)) {
    return;
  }

  showBackgroundHighlight.value = true;
  const HIGHLIGHT_TIMER = 1000;
  useTimeoutFn(() => {
    showBackgroundHighlight.value = false;
  }, HIGHLIGHT_TIMER);
};

function handleOpenMessageInfo({ messageId } = {}) {
  if (Number(messageId) !== Number(props.id)) return;
  if (!contextMenuEnabledOptions.value.info) return;
  contextMenuRef.value?.handleMessageInfo?.();
}

onMounted(() => {
  setupHighlightTimer();
  emitter.on(
    BUS_EVENTS.MESSAGE_INTERACTION_OPENED,
    handleMessageInteractionOpened
  );
  emitter.on(BUS_EVENTS.OPEN_MESSAGE_INFO, handleOpenMessageInfo);
  emitter.on(BUS_EVENTS.REACTION_EMOJI_SELECTED, handleReactionEmojiSelected);
});

onBeforeUnmount(() => {
  cancelLongPress();
  removeFocusedMessageClone();
  clearSuppressedClick();
  closeQuickReact();
  emitter.off(
    BUS_EVENTS.MESSAGE_INTERACTION_OPENED,
    handleMessageInteractionOpened
  );
  emitter.off(BUS_EVENTS.OPEN_MESSAGE_INFO, handleOpenMessageInfo);
  emitter.off(BUS_EVENTS.REACTION_EMOJI_SELECTED, handleReactionEmojiSelected);
});

// Aceleres: o nome de quem falou no grupo agora é renderizado como rótulo da
// bolha, a partir de content_attributes. As mensagens JÁ GRAVADAS, porém,
// carregam esse nome cravado no início do próprio texto ("Bruno:\n…") — três
// ingestões diferentes fizeram isso, cada uma com sua marcação. Sem remover o
// prefixo aqui, o histórico apareceria com o nome duas vezes: no rótulo e no
// corpo.
//
// A limpeza acontece na entrega do contexto, e não em cada bolha, porque texto,
// legenda de mídia, citação e menu de contexto leem todos deste mesmo `content`
// — corrigi-los um a um deixaria alguma superfície para trás.
const displayContent = computed(() => {
  // Algumas midias antigas nao carregam `is_group` nem o JID do grupo na
  // propria mensagem. A conversa continua sendo a fonte confiavel para que o
  // nome legado saia da legenda e permaneça somente no cabecalho da bolha.
  const conversation = conversationGetter.value(props.conversationId);

  return stripGroupSenderPrefix(
    props.content,
    props.contentAttributes,
    isGroupConversation(conversation)
  );
});

provideMessageContext({
  ...toRefs(props),
  content: displayContent,
  canDelete: computed(() => contextMenuEnabledOptions.value.delete),
  isPrivate: computed(() => props.private),
  variant,
  orientation,
  isBotOrAgentMessage,
  shouldGroupWithNext,
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="shouldRenderMessage"
    :id="`message${props.id}`"
    ref="messageContainerRef"
    class="message-bubble-container acl-mobile-tip-clearance group/context-menu relative mb-1 flex w-full before:pointer-events-none before:absolute before:-left-16 before:-right-12 before:top-[-1px] before:-z-[1] before:content-['']"
    :data-message-id="props.id"
    :data-attachment-type="attachments?.[0]?.fileType || 'none'"
    :data-outgoing-image="isOutgoingImageMessage ? 'true' : undefined"
    :class="[
      flexOrientationClass,
      {
        // Keep the highlight on the bubble, excluding row padding and reaction reserve.
        'before:bottom-[42px]': rendersExternalReactions,
        'before:bottom-[11px]': !rendersExternalReactions,
        'aceleres-react-open': showQuickReact,
        'aceleres-context-open': showContextMenu,
        'max-md:!mb-0': isStickerBubble || isStickerAlbumBubble,
        'group-with-next': shouldGroupWithNext,
        'bg-n-alpha-1': showBackgroundHighlight,
        'isolate max-md:before:!bg-transparent':
          isSelectionMode && variant !== MESSAGE_VARIANTS.ACTIVITY,
        'acl-message-selected items-center before:bg-[rgba(0,128,105,0.08)]':
          isSelected,
        '[@media(hover:hover)_and_(pointer:fine)]:hover:items-center [@media(hover:hover)_and_(pointer:fine)]:hover:before:bg-[rgba(0,128,105,0.05)]':
          isSelectionMode &&
          variant !== MESSAGE_VARIANTS.ACTIVITY &&
          !isSelected,
        'message--selectable cursor-pointer':
          isSelectionMode && variant !== MESSAGE_VARIANTS.ACTIVITY,
        'max-md:!m-0 max-md:!w-full max-md:!bg-transparent max-md:!p-0':
          isSelectionMode,
      },
    ]"
    @click.capture="handleMessageClick"
    @dragstart.capture="preventMobileMediaDrag"
  >
    <div
      v-if="isSelectionMode && variant !== MESSAGE_VARIANTS.ACTIVITY"
      class="acl-message-select-checkbox flex w-10 flex-shrink-0 items-center justify-center self-center md:w-12"
    >
      <span
        class="flex size-5 items-center justify-center rounded-full border-2 transition-colors md:size-6 md:rounded"
        :class="
          isSelected
            ? 'border-[#008069] bg-[#008069] text-white md:border-[#000] md:bg-[#000]'
            : 'border-n-slate-7 bg-transparent hover:border-n-slate-10'
        "
      >
        <span v-if="isSelected" class="i-lucide-check size-4" />
      </span>
    </div>
    <div v-if="variant === MESSAGE_VARIANTS.ACTIVITY" class="w-full">
      <ActivityBubble :content="content" />
    </div>
    <div
      v-else
      :class="[
        gridClass,
        {
          'gap-y-2': contentAttributes.externalError,
          'w-full': isSelectionMode || variant === MESSAGE_VARIANTS.EMAIL,
        },
      ]"
      class="gap-x-2"
      :style="{
        gridTemplateAreas: gridTemplate,
      }"
    >
      <div
        v-if="!shouldGroupWithNext && shouldShowAvatar"
        v-tooltip.left-end="avatarTooltip"
        class="acl-message-avatar [grid-area:avatar] flex items-end"
      >
        <Avatar v-bind="avatarInfo" :size="24" />
      </div>
      <div
        class="acl-message-bubble-row [grid-area:bubble] flex pb-2 select-none md:select-text"
        :class="{
          'ltr:ml-8 rtl:mr-8 justify-end': orientation === ORIENTATION.RIGHT,
          'ltr:mr-8 rtl:ml-8': orientation === ORIENTATION.LEFT,
          'max-md:ltr:!ml-[47px] max-md:rtl:!mr-[47px]':
            orientation === ORIENTATION.RIGHT && shouldShowForwardButton,
          'max-md:ltr:!mr-[47px] max-md:rtl:!ml-[47px]':
            orientation === ORIENTATION.LEFT && shouldShowForwardButton,
          'min-w-0': variant === MESSAGE_VARIANTS.EMAIL,
          'acl-bubble-tight-pb': !rendersExternalReactions,
        }"
        @contextmenu="openContextMenu($event)"
        @touchstart.passive="onTouchStart"
        @touchend="onTouchEnd"
        @touchcancel="onTouchEnd"
        @touchmove.passive="onTouchMove"
      >
        <div
          class="aceleres-bubble-with-actions relative inline-block max-w-full"
          :class="{
            'acl-outgoing-image-wrapper': isOutgoingImageMessage,
            'acl-private-text-note-mobile-wrapper max-md:ltr:mr-3 max-md:rtl:ml-3': isPrivateTextNote,
            'mb-5': rendersExternalReactions,
          }"
          :style="{
            marginLeft: isOutgoingImageMessage ? 'auto' : undefined,
            marginRight: isOutgoingImageMessage ? '0' : undefined,
          }"
        >
          <!-- Aceleres: shadow movido pro .left-bubble / .right-bubble via
               filter:drop-shadow (em Message.vue style), pra envolver a ponta
               do balao. Aqui no wrapper interno mantemos so o rounded. -->
          <!-- A bolha de emoji nao tem fundo, entao a seta caia em cima do
               proprio desenho. A seta fica EXATAMENTE como nas outras bolhas —
               mesmo tamanho, mesma cor, mesma espessura, sem halo: o que muda e
               so o sitio. O emoji ocupa 60x60 (`.cw-emoji--single-message`),
               logo `-right-3` desloca-a para fora dele e `top-0` alinha-a pelo
               topo, onde nao e cortada pelo recorte da lista.

               A condicao vive no seletor, e nao num computed, porque quem
               decide que a bolha e de emoji e o filho, depois do parse do
               twemoji. -->
          <div
            class="aceleres-bubble-surface relative inline-flex max-w-full rounded-xl [&:has(>.acl-emoji-bubble.left-bubble)>.aceleres-bubble-chevron]:!top-0 [&:has(>.acl-emoji-bubble.left-bubble)>.aceleres-bubble-chevron]:!-right-3"
            :class="{ '!max-w-[min(85vw,28rem)]': isWameMediaPending }"
          >
            <Component
              :is="componentToRender"
              v-if="!isWameMediaPending"
              :class="
                isMessageDeleted
                  ? 'border border-n-weak !bg-transparent text-n-slate-11'
                  : undefined
              "
            >
              <template
                v-if="
                  shouldShowContextMenu &&
                  isBubble &&
                  hasAdaptiveMediaMenuSlot &&
                  (!isMobileViewport || !isStickerBubble)
                "
                #menu-trigger="{ chevronTone }"
              >
                <button
                  type="button"
                  aria-label="Abrir menu da mensagem"
                  class="aceleres-bubble-chevron"
                  :class="[
                    chevronStyleClasses,
                    adaptiveChevronToneClasses(chevronTone),
                    orientation === 'right'
                      ? 'is-right-bubble'
                      : 'is-left-bubble',
                  ]"
                  @click.stop="openContextMenu($event, $event.currentTarget)"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    style="pointer-events: none; display: block"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
              </template>
            </Component>
            <WameMediaProgress v-else />
            <button
              v-if="
                shouldShowContextMenu &&
                isBubble &&
                !hasAdaptiveMediaMenuSlot &&
                !isStickerAlbumBubble &&
                (!isMobileViewport || !isStickerBubble)
              "
              type="button"
              aria-label="Abrir menu da mensagem"
              class="aceleres-bubble-chevron"
              :class="[
                chevronStyleClasses,
                orientation === 'right' ? 'is-right-bubble' : 'is-left-bubble',
              ]"
              @click.stop="openContextMenu($event, $event.currentTarget)"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                style="pointer-events: none; display: block"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          </div>
          <!-- A reação usa uma única âncora externa em todos os tipos de
               mensagem: enviada à direita e recebida à esquerda. -->
          <MessageReactions
            v-if="rendersExternalReactions"
            :reactions="contentAttributes.reactions"
            class="absolute -bottom-5 z-10 flex"
            :class="orientation === ORIENTATION.RIGHT ? 'right-2' : 'left-2'"
            @open="openReactionsModal"
          />
          <!-- Aceleres: botões de hover ao lado da bolha. Ordem visual esq→dir
               [smile][forward][bubble] (reagir primeiro, encaminhar à direita do
               smile — pedido 2026-07-03). DOM segue a mesma ordem p/ Tab/leitor de
               tela. Ambos position:absolute, então só o foco depende da ordem.
               Suprimidos pro par de stickers (!isStickerAlbumBubble) pelo mesmo
               motivo do chevron acima: agiriam só na ÚLTIMA mensagem do par,
               enganoso pra um card que representa 2 mensagens. -->
          <button
            v-if="
              shouldShowContextMenu &&
              isBubble &&
              !isStickerAlbumBubble &&
              inbox.channel_type !== 'Channel::Instagram' &&
              contextMenuEnabledOptions.react
            "
            type="button"
            aria-label="Reagir à mensagem"
            class="aceleres-bubble-smile"
            :class="[
              orientation === 'right' ? 'is-right-bubble' : 'is-left-bubble',
              {
                'is-active': showQuickReact,
                'is-without-forward': !shouldShowForwardButton,
              },
            ]"
            @click.stop="toggleQuickReact($event)"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              style="pointer-events: none; display: block"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M8 14s1.5 2 4 2 4-2 4-2" />
              <line x1="9" y1="9" x2="9.01" y2="9" />
              <line x1="15" y1="9" x2="15.01" y2="9" />
            </svg>
          </button>
          <button
            v-if="shouldShowForwardButton"
            type="button"
            aria-label="Encaminhar mensagem"
            class="aceleres-bubble-forward"
            :class="[
              orientation === 'right' ? 'is-right-bubble' : 'is-left-bubble',
            ]"
            @click.stop="forwardThisMessage"
          >
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="currentColor"
              class="aceleres-forward-mobile-icon pointer-events-none md:hidden"
              aria-hidden="true"
            >
              <path
                d="M14 8V4l7 7-7 7v-4.1c-5 0-8.5 1.6-11 5.1 1-5 4-10 11-11z"
              />
            </svg>
            <Icon
              icon="i-tabler-corner-up-right-double"
              class="pointer-events-none hidden size-5 md:block"
            />
          </button>
          <Teleport to="body">
            <Transition name="aceleres-quick-pill">
              <div
                v-if="showQuickReact && inbox.channel_type !== 'Channel::Instagram'"
                ref="quickReactRef"
                class="aceleres-quick-react-pill"
                :class="[
                  `is-${pillDirection}`,
                  { 'is-picker-open': initialShowPicker },
                ]"
                :style="pillStyle"
                @click.stop
              >
                <MessageReactionBar
                  :channel-type="inbox.channel_type"
                  @react="handleQuickReact"
                  @more="openFullPickerFromPill"
                  @close="closeQuickReactAndPicker"
                />
              </div>
            </Transition>
          </Teleport>
        </div>
      </div>
      <Teleport to="body">
        <MessageReactionsModal
          v-if="showReactionsModal"
          :channel-type="inbox.channel_type"
          :reactions="(contentAttributes && contentAttributes.reactions) || []"
          :current-user-id="currentUserId"
          :anchor-el="reactionsAnchorEl"
          :orientation="orientation"
          @react="handleReaction"
          @close="showReactionsModal = false"
        />
      </Teleport>
      <!-- Aceleres: `externalError` só existe em mensagem que chegou a ser
           criada e o PROVEDOR recusou. A recusa do próprio painel (portão de
           envio devolvendo 423/422 antes de criar a mensagem) deixa só
           `status: failed` no store — e como o MessageMeta esconde o tique de
           status para `failed` "porque o erro já aparece", o balão ficava
           idêntico a uma mensagem entregue. Foi o relato de 09/09/2026: duas
           tentativas recusadas lidas como dois envios ao cliente. -->
      <MessageError
        v-if="
          contentAttributes.externalError || status === MESSAGE_STATUS.FAILED
        "
        class="[grid-area:meta] acl-message-error"
        :class="[flexOrientationClass]"
        @retry="emit('retry')"
      />
    </div>
    <!-- Aceleres: `in-reply-to` chega até aqui porque o modal "Informações da
         mensagem" desenha a citação como o balão desenha. A mensagem citada já vem
         resolvida do MessageList (getInReplyToMessage), inclusive quando precisou
         ser buscada na API. -->
    <div v-if="shouldShowContextMenu" class="context-menu-wrap">
      <ContextMenu
        v-if="isBubble"
        ref="contextMenuRef"
        :context-menu-position="contextMenuPosition"
        :is-open="showContextMenu"
        :enabled-options="contextMenuEnabledOptions"
        :message="payloadForContextMenu"
        :in-reply-to="inReplyTo"
        :selection-messages="contextMenuSelectionMessages"
        hide-button
        :initial-show-picker="initialShowPicker"
        :current-user-id="currentUserId"
        @open="openContextMenu"
        @close="closeContextMenu"
        @reply-to="handleReplyTo"
        @react="handleQuickReact"
        @open-quick-react="openQuickReactFromContextMenu"
      />
    </div>
  </div>
</template>

<style lang="scss">
/* As duas pontas saem da bolha no mobile; a linha deixa espaço para ambas. */
@media (max-width: 767px) {
  .acl-whatsapp-mobile-scope
    .message-bubble-container.acl-mobile-tip-clearance {
    padding-inline: 16px !important;
  }
}

/* Aceleres: cantos superiores sempre arredondados (mesmo em mensagens
   agrupadas), pra todas as bolhas ficarem com cantos top como os outros. */
.group-with-next + .message-bubble-container {
  .left-bubble {
    @apply ltr:rounded-tl-xl rtl:rounded-tr-xl;
  }

  .right-bubble {
    @apply ltr:rounded-tr-xl rtl:rounded-tl-xl;
  }
}

/* Aceleres: bico de balao tipo WhatsApp/ChatGPT no canto inferior do bubble.
   Em bubbles de midia (Image/Video/etc) o BaseBubble vem com overflow-hidden;
   sobrepomos pra que o ::after possa sair pra fora do bubble. Os elementos
   internos (imagens etc) ja vem com rounded proprio, entao nao quebra layout. */
.left-bubble,
.right-bubble {
  overflow: visible !important;
}
.aceleres-bubble-surface {
  overflow: visible !important;
}
/* A sombra acompanha o contorno inteiro do balão, inclusive o bico. */
.left-bubble,
.right-bubble {
  filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.12))
    drop-shadow(0 5px 8px rgba(0, 0, 0, 0.12));
}
.left-bubble::after,
.right-bubble::after {
  content: '';
  position: absolute;
  background-color: inherit;
  pointer-events: none;
  width: 7px;
  height: 12px;
}
/* LEFT bubble: bico no canto SUPERIOR esquerdo (msg recebida).
   border-bottom-right-radius arredonda a quina inferior interna do pseudo,
   deixando o canto top-left bem agudo (a "ponta" do bico). Encaixado no
   canto top-left da bolha (rounded-tl zerado). */
.left-bubble {
  position: relative !important;
  overflow: visible !important;
  border-top-left-radius: 8px !important;
}
.left-bubble::after {
  width: 12px !important;
  height: 14px !important;
  background-color: #fff !important;
  left: -7px !important;
  top: 0 !important;
  z-index: 3 !important;
  pointer-events: none !important;
  clip-path: polygon(100% 0, 100% 100%, 0 0) !important;
}
/* RIGHT bubble: bico espelhado no canto SUPERIOR direito (msg enviada),
   alinhado ao visual do bubble verde principal. */
.right-bubble {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 12px !important;
  font-size: 0.975rem;
}
.right-bubble::after {
  width: 24px !important;
  height: 12px !important;
  top: 0 !important;
  bottom: auto !important;
  right: -12px !important;
  left: auto !important;
  z-index: 3 !important;
  pointer-events: none !important;
  border-top-left-radius: 0 !important;
  clip-path: polygon(0 0, 50% 100%, 100% 12%, 100% 0) !important;
}
/* Figurinha e emoji nao tem fundo de balao: o bico branco/verde do
   ::after ficava solto no canto (o "puntito" da figurinha recebida).
   Sem fundo nao ha bico a apontar. */
.left-bubble[data-bubble-name='sticker']::after,
.left-bubble[data-bubble-name='sticker-album']::after,
.left-bubble[data-bubble-name='emoji']::after,
.right-bubble[data-bubble-name='sticker']::after,
.right-bubble[data-bubble-name='sticker-album']::after {
  display: none !important;
}
/* Aceleres: 12px de respiro entre stickers, contados a partir do HORARIO
   sobreposto no canto do sticker — nao da borda da imagem em si (o horario
   fica ~16.5px acima da borda real, entao medir a partir da imagem sempre
   sobra espaco demais). Ver Base.vue: pra bolha de sticker o horario cola
   na quina (`bottom-0` em vez do `bottom-2.5` padrao), o que deixa so 4px
   entre o texto do horario e a borda da bolha.

   Contas verificadas rendendo o CSS compilado de verdade + stickers reais
   (nao so lendo o Tailwind): com o horario colado, falta 8px de fora pra
   fechar 12px a partir dele. Desses 8px, 4px ja vem do padding-bottom da
   `.acl-message-bubble-row` (`.acl-bubble-tight-pb`, 0.2rem); os outros
   4px sao este margin-bottom.

   Vai em PIXEL puro de proposito, sem `calc()`: o build (cssnano/postcss-
   calc) reduz `calc(12px - 0.2rem)` errado, descartando o termo em rem e
   deixando so `12px` no CSS final — silencioso, sem warning. Reaproveita
   o `[data-bubble-name='sticker']` que o Image.vue ja emite — mesmo
   seletor da regra do halo acima —, entao nao ha classe nova nem marcacao
   duplicada.

   A reação externa reserva o próprio espaço no wrapper da bolha, portanto
   esta margem continua exclusiva do sticker sem reação. */
.message-bubble-container:has([data-bubble-name='sticker']) {
  margin-bottom: 4px;
}
/* Aceleres: quando NAO tem reacao, o wrapper [grid-area:bubble] vai com
   padding-bottom mais apertado pra mensagens ficarem mais compactas. */
.acl-bubble-tight-pb {
  padding-bottom: 0.2rem !important;
}

/* Aceleres: halo radial atras da flechinha. O pseudo cobre a bolha
   INTEIRA (inset:0 + border-radius:inherit), e o gradient e definido com
   `radial-gradient(circle Xpx at <chevron-center>, ...)` — ou seja, uma
   forma circular nativa que se esvai pra transparente progressivamente.
   Como o pseudo cobre todo o card e o gradiente termina em transparent,
   nao ha bordas/retangulos visiveis em lugar nenhum. */
.left-bubble::before,
.right-bubble::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  opacity: 0;
  transition: opacity 0.18s ease;
  pointer-events: none;
  z-index: 10;
}
/* No left-bubble (branco) o halo e branco.
   Pedido 2026-09-14 (facundo): branco SOLIDO nos ~21px em volta da seta e
   esmaecendo ate 70px, para tapar o texto que chega ao canto. */
.left-bubble::before {
  background: radial-gradient(
    circle 70px at calc(100% - 19px) 17px,
    var(--acl-chevron-halo-strong, rgba(255, 255, 255, 1)) 0%,
    var(--acl-chevron-halo-medium, rgba(255, 255, 255, 1)) 30%,
    var(--acl-chevron-halo-soft, rgba(255, 255, 255, 0.6)) 60%,
    transparent 100%
  );
}
/* O halo nunca tapava o texto por causa do "fix ghost text" em
   DASHBOARD_SCRIPTS (banco): ele poe `.message-bubble-container .prose-bubble`
   em `position: relative; z-index: 20`, acima dos 10 deste pseudo. Medido no
   navegador em 14/09. No texto RECEBIDO o halo sobe acima do texto.
   Pedido 2026-09-15 (facundo): o horario (`.acl-bubble-meta`) fica ABAIXO do
   halo, como antes. Numa bolha curta a hora passa por baixo da seta, e com a
   hora acima do halo (z-index 31) a seta aparecia desenhada sobre "10:00" sem
   o fundo branco. */
.message-bubble-container .left-bubble[data-bubble-name='text']::before {
  z-index: 30;
}
/* Nas mídias recebidas, o mesmo halo do texto passa a ser escuro para dar
   contraste à seta branca sem aplicar sombra no próprio ícone. */
.left-bubble:is(
    [data-bubble-name='image'],
    [data-bubble-name='image-album'],
    [data-bubble-name='video'],
    [data-bubble-name='attachment'],
    [data-bubble-name='gif'],
    [data-bubble-name='sticker'],
    [data-bubble-name='sticker-album']
  ) {
  --acl-chevron-halo-strong: rgba(0, 0, 0, 0.58);
  --acl-chevron-halo-medium: rgba(0, 0, 0, 0.3);
  --acl-chevron-halo-soft: rgba(0, 0, 0, 0.1);
}
/* No right-bubble (verde #d9fdd3) o halo usa o mesmo verde claro da
   propria bolha, so que mais concentrado, completamente circular e
   com fade gradativo pra transparent — nenhum corte/retangulo. */
.right-bubble::before {
  background: radial-gradient(
    circle 70px at calc(100% - 19px) 17px,
    var(--acl-chevron-halo-strong, rgba(217, 253, 211, 0.9)) 0%,
    var(--acl-chevron-halo-medium, rgba(217, 253, 211, 0.5)) 35%,
    var(--acl-chevron-halo-soft, rgba(217, 253, 211, 0.2)) 65%,
    transparent 100%
  );
}
.acl-private-note-bubble {
  --acl-chevron-halo-strong: rgb(var(--amber-4) / 0.9);
  --acl-chevron-halo-medium: rgb(var(--amber-4) / 0.5);
  --acl-chevron-halo-soft: rgb(var(--amber-3) / 0.2);
}
@media (hover: hover) and (pointer: fine) {
  .aceleres-bubble-surface:hover .left-bubble::before,
  .aceleres-bubble-surface:hover .right-bubble::before {
    opacity: 1;
  }
}
/* Áudio, embed, story e emoji continuam sem halo. As mídias recebidas
   listadas acima agora usam o sombreado escuro; nas enviadas, o comportamento
   anterior permanece desativado. */
.left-bubble[data-bubble-name='audio']::before,
.left-bubble[data-bubble-name='embed']::before,
.left-bubble[data-bubble-name='ig-story']::before,
.left-bubble[data-bubble-name='emoji']::before,
.left-bubble[data-bubble-name='wame-media-progress']::before,
.right-bubble[data-bubble-name='image']::before,
.right-bubble[data-bubble-name='video']::before,
.right-bubble[data-bubble-name='audio']::before,
.right-bubble[data-bubble-name='embed']::before,
.right-bubble[data-bubble-name='attachment']::before,
.right-bubble[data-bubble-name='ig-story']::before,
.right-bubble[data-bubble-name='gif']::before,
.right-bubble[data-bubble-name='sticker']::before,
.right-bubble[data-bubble-name='sticker-album']::before,
.right-bubble[data-bubble-name='emoji']::before,
.right-bubble[data-bubble-name='wame-media-progress']::before {
  display: none;
}

/* Aceleres: flechinha no canto sup-direito da bolha. Estilo WhatsApp:
   um pequeno patch translúcido encostado no topo direito da bolha — dá
   sensação de "vir de dentro da bolha" e fica sutil sem chamar atenção. */
.aceleres-bubble-chevron {
  position: absolute;
  top: 6px;
  right: 8px;
  z-index: 20;
  width: 24px;
  height: 24px;
  background: transparent;
  color: rgba(0, 0, 0, 0.87);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  cursor: pointer;
  border: none;
  border-radius: 0;
  padding: 0;
  transition:
    opacity 0.12s,
    visibility 0.12s,
    filter 0.12s;
}
/* Na bolha de emoji a seta encosta no canto (o `!top-1.5` que ja vem de
   `chevronStyleClasses`), para nao cair em cima do proprio desenho. A excecao
   e quando a barra do nome do remetente existe mesmo — so em grupo, e por
   isso NAO se pode fixar o recuo: em conversa a dois a barra nao e desenhada
   e o recuo empurrava a seta para o meio do emoji. Com a barra presente, a
   seta de 24px desce para partilhar o centro vertical de 21px dela. */
.aceleres-bubble-surface:has(> .acl-emoji-bubble .acl-sender-name)
  > .aceleres-bubble-chevron {
  top: 9px !important;
}
/* Igual ao WhatsApp: a seta aparece apenas sobre o card da mensagem. */
.aceleres-bubble-surface:hover .aceleres-bubble-chevron,
.aceleres-bubble-chevron:focus-visible {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
  outline: none;
}

/* Aceleres: wrapper relativo que abriga o bubble + o smile (que fica fora
   do `.inline-flex` do bubble). Inline-block pra não esticar a row. */
div.\[grid-area\:bubble\] > .aceleres-bubble-with-actions {
  max-width: 50% !important;
}

/* Aceleres: imagens enviadas devem manter o wrapper no tamanho real do
   bubble. Se o wrapper ocupa a coluna inteira, os controles absolutos de
   reação/encaminhamento se ancoram longe da imagem. */
@media (min-width: 768px) {
  .acl-outgoing-image-wrapper {
    display: inline-flex !important;
    width: fit-content !important;
    max-width: min(50%, 408px) !important;
    flex: 0 0 auto !important;
    align-items: flex-start !important;
  }

  .acl-outgoing-image-wrapper > .aceleres-bubble-surface {
    display: inline-flex !important;
    width: fit-content !important;
    max-width: 100% !important;
    flex: 0 0 auto !important;
  }
}

.aceleres-bubble-with-actions {
  /* nada além do relative/inline-block — só serve de âncora pro smile */
}

/* Modo seleção: esconder os botões de reação (smile) e encaminhar (forward)
   que apareceriam no hover da bolha. */
.message--selectable .aceleres-bubble-smile,
.message--selectable .aceleres-bubble-forward {
  display: none !important;
}

/* Aceleres: smile redondo FORA do bubble, do lado oposto à orientação:
   à esquerda para bubble outgoing (right-bubble), à direita para incoming
   (left-bubble). Aparece no hover da bolha — clica e abre a pill rápida. */
.aceleres-bubble-smile,
.aceleres-bubble-forward {
  width: 40px;
  height: 40px;
}

.aceleres-bubble-smile {
  position: absolute;
  top: 50%;
  z-index: 20;
  background: rgba(255, 255, 255, 0.96);
  color: rgba(0, 0, 0, 0.62);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  padding: 0;
  transition:
    opacity 0.12s,
    transform 0.12s,
    filter 0.12s,
    background 0.12s;
  box-shadow:
    0 2px 6px rgba(0, 0, 0, 0.14),
    0 1px 2px rgba(0, 0, 0, 0.08);
  transform: translateY(-50%);
}
/* Bubble outgoing (à direita) → smile na ESQUERDA da bolha.
   Aceleres 2026-07-03: ordem visual [smile][forward][bubble] (WhatsApp-like:
   reagir primeiro, encaminhar à direita do smile). O smile fica mais afastado
   da bolha; o forward cola nele pelo lado interno. */
.aceleres-bubble-smile.is-right-bubble {
  right: 100%;
  left: auto;
  margin-right: 60px;
  z-index: 25;
}
/* Bubble incoming (à esquerda) → smile na DIREITA da bolha. Ordem espelhada
   [bubble][forward][smile]: mesmos 60px do lado outgoing, agora que o forward
   também mora aqui (antes era 14px, quando só o smile ocupava o espaço). */
.aceleres-bubble-smile.is-left-bubble {
  left: 100%;
  right: auto;
  margin-left: 60px;
  z-index: 25;
}
/* Aceleres 2026-08-11: os 60px acima reservam espaço pro forward colado ao
   lado do smile — sem forward (sticker, video sem ser GIF, audio, texto),
   sobra um vão vazio grande demais até a bolha. Aqui encolhe pra 14px,
   mesma margem que o forward teria usado sozinho. */
.aceleres-bubble-smile.is-right-bubble.is-without-forward {
  margin-right: 14px;
}
.aceleres-bubble-smile.is-left-bubble.is-without-forward {
  margin-left: 14px;
}
.message-bubble-container:hover .aceleres-bubble-smile,
.aceleres-bubble-smile:hover,
.aceleres-bubble-smile:focus,
.aceleres-bubble-smile.is-active {
  opacity: 1;
  outline: none;
}
.aceleres-bubble-smile:hover,
.aceleres-bubble-smile.is-active {
  transform: translateY(-50%) scale(1.08);
  background: white;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}
.dark .aceleres-bubble-smile {
  background: rgba(40, 40, 40, 0.96);
  color: rgba(255, 255, 255, 0.8);
  border-color: rgba(255, 255, 255, 0.1);
}
.dark .aceleres-bubble-smile:hover,
.dark .aceleres-bubble-smile.is-active {
  background: rgba(60, 60, 60, 0.98);
}

/* Aceleres: seta de encaminhar (forward) ao lado do smile — outgoing E
   incoming (pedido 2026-08-11: antes era so outgoing). Mesmo visual redondo
   do smile. Fica imediatamente ao lado interno do smile (entre smile e
   bolha): ordem [smile][forward][bubble] no outgoing, espelhada
   [bubble][forward][smile] no incoming. Aparece no hover. */
.aceleres-bubble-forward {
  position: absolute;
  top: 50%;
  z-index: 25;
  background: rgba(255, 255, 255, 0.96);
  color: rgba(0, 0, 0, 0.62);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  padding: 0;
  transition:
    opacity 0.12s,
    transform 0.12s,
    filter 0.12s,
    background 0.12s;
  box-shadow:
    0 2px 6px rgba(0, 0, 0, 0.14),
    0 1px 2px rgba(0, 0, 0, 0.08);
  transform: translateY(-50%);
}
/* Outgoing (à direita): forward cola no lado esquerdo da bolha; smile fica
   40px+6px além dele (60px de margem, ver .is-right-bubble do smile). */
.aceleres-bubble-forward.is-right-bubble {
  right: 100%;
  left: auto;
  margin-right: 14px;
}
/* Incoming (à esquerda): espelhado — forward cola no lado direito da bolha. */
.aceleres-bubble-forward.is-left-bubble {
  left: 100%;
  right: auto;
  margin-left: 14px;
}
.message-bubble-container:hover .aceleres-bubble-forward,
.aceleres-bubble-forward:hover,
.aceleres-bubble-forward:focus {
  opacity: 1;
  outline: none;
}
.aceleres-bubble-forward:hover {
  transform: translateY(-50%) scale(1.08);
  background: white;
  color: #15603e;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}
.dark .aceleres-bubble-forward {
  background: rgba(40, 40, 40, 0.96);
  color: rgba(255, 255, 255, 0.8);
  border-color: rgba(255, 255, 255, 0.1);
}
.dark .aceleres-bubble-forward:hover {
  background: rgba(60, 60, 60, 0.98);
}

/* Aceleres: pill rápida de reação (estilo WhatsApp). Aparece ACIMA do botão
   smile, centralizada e com gap mínimo. 6 emojis + "+" — o "+" abre o
   picker completo. */

/* Aceleres: a quick-react-pill agora é <Teleport to="body"> + position:fixed
   (mesmo padrão do emoji-picker). As coords (left/top/bottom) vêm inline do
   positionQuickReactPill(), calculadas a partir do rect do smile — então este
   bloco só define o visual; NÃO posiciona. z-index alto (abaixo só do topbar
   9999/picker 9999) garante que ela abre por cima de tudo. (2026-07-02) */
.message-bubble-container.aceleres-react-open {
  z-index: 40;
}
.message-bubble-container.aceleres-context-open {
  z-index: 39;
}

/* Aceleres 2026-08-17: acima do compositor. O shell do compositor
   (MessagesView.vue) e um contexto de empilhamento com z-index 2147482000 e
   `isolation: isolate`, entao qualquer menu na casa dos milhares — mesmo
   teleportado para o body — era pintado ATRAS dele e ficava escondido ou
   inclicavel na parte de baixo da conversa.

   2147482700 nao e um numero novo: e a mesma faixa que os menus flutuantes do
   mobile ja usam (aceleres-overrides.css), acima do compositor e abaixo do
   aviso de "digitando" (2147483000) e dos modais (2147483500+). */
.aceleres-quick-react-pill {
  position: fixed;
  z-index: 2147482700;
  background: white;
  border-radius: 50px;
  box-shadow:
    0 6px 16px rgba(0, 0, 0, 0.18),
    0 2px 4px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.06);
  padding: 0 1px;
  display: flex;
  align-items: center;
  transform: translateX(-50%);
  transform-origin: center center;
  will-change: transform;
}
/* Aceleres: ícones de reação rápida (emoji + botão "+") em tamanho normal
   dentro da pill — botões de 24px com emoji de 1.75rem. Botão "+" com ícone plus limpo
   (i-lucide-plus) e SEM background (nem no hover) — só o ícone.
   (pedido 2026-07-01) */
.aceleres-quick-react-pill :deep(.aceleres-emoji-btn) {
  width: 1.75rem !important;
  height: 1.75rem !important;
  font-size: 0 !important;
  overflow: visible !important;
}
.aceleres-quick-react-pill
  :deep(.aceleres-emoji-btn:not(.aceleres-emoji-btn--more) span) {
  width: 2rem !important;
  height: 2rem !important;
  font-size: 2rem !important;
}
.aceleres-quick-react-pill
  :deep(.aceleres-emoji-btn:not(.aceleres-emoji-btn--more) .cw-emoji) {
  display: block !important;
  width: 2rem !important;
  height: 2rem !important;
}
.aceleres-quick-react-pill :deep(.aceleres-emoji-btn--more span) {
  width: 1rem !important;
  height: 1rem !important;
  font-size: 1rem !important;
}
.aceleres-quick-react-pill :deep(.aceleres-emoji-btn--more),
.aceleres-quick-react-pill :deep(.aceleres-emoji-btn--more:hover) {
  background: transparent !important;
  background-color: transparent !important;
}
/* Aceleres: a pill aberta acima da mensagem precisa de reações mais legíveis. */
.aceleres-quick-react-pill.is-up :deep(.aceleres-emoji-btn) {
  width: 2.5rem !important;
  height: 2.5rem !important;
}
.aceleres-quick-react-pill.is-up :deep(.aceleres-emoji-btn--more span) {
  width: 1.5rem !important;
  height: 1.5rem !important;
  font-size: 1.5rem !important;
}
/* A pill é centralizada no smile e cresce no próprio centro. */
/* Com o seletor completo aberto, a pilula continua acima dele para que as
   reacoes rapidas permaneçam visiveis e acessiveis. */
.aceleres-quick-react-pill.is-picker-open {
  pointer-events: none;
  z-index: 2147482701;
}
.aceleres-quick-react-pill.is-picker-open :deep(.aceleres-emoji-btn) {
  pointer-events: auto;
}
.dark .aceleres-quick-react-pill {
  background: #2a2a2a;
  border-color: rgba(255, 255, 255, 0.08);
}
.aceleres-quick-pill-enter-active {
  transition:
    transform 0.2s cubic-bezier(0.16, 1, 0.3, 1),
    opacity 0.16s ease-out;
}
.aceleres-quick-pill-leave-active {
  transition:
    transform 0.15s ease-in,
    opacity 0.15s ease-in;
}
.aceleres-quick-pill-enter-from,
.aceleres-quick-pill-leave-to {
  opacity: 0.65;
  transform: translateX(-50%) scale(0.65);
}

@media (hover: none), (pointer: coarse) {
  .acl-message-bubble-row {
    -webkit-touch-callout: none;
  }

  .aceleres-bubble-smile,
  .aceleres-bubble-forward,
  .aceleres-bubble-chevron {
    display: none !important;
  }
}

@media (max-width: 767px) {
  .aceleres-bubble-forward {
    display: flex !important;
    opacity: 1;
  }

  .aceleres-bubble-forward svg {
    width: 22px;
    height: 22px;
  }
}

@media (max-width: 380px) {
  .aceleres-quick-react-pill {
    max-width: calc(100vw - 16px);
    padding-right: 2px;
    padding-left: 2px;
  }

  .aceleres-quick-react-pill :deep(.aceleres-emoji-btn) {
    width: 1.75rem !important;
    height: 1.75rem !important;
    font-size: 0 !important;
  }

  /* Pedido 2026-09-14 (facundo): 7 caritas visiveis + o "+" = 8 botoes
     (era 6 + "+" = 7). As restantes deslizam em MessageReactionBar.vue. */
  .aceleres-quick-react-pill.is-up :deep(.aceleres-emoji-btn) {
    width: calc((100vw - 26px) / 8) !important;
    height: calc((100vw - 26px) / 8) !important;
  }

}

@media (prefers-reduced-motion: reduce) {
  .aceleres-quick-pill-enter-active,
  .aceleres-quick-pill-leave-active,
  .aceleres-emoji-btn {
    transition-duration: 0.01ms !important;
  }
}
</style>
